import { initializeApp } from 'firebase/app';
import { getAuth, onAuthStateChanged, signOut } from 'firebase/auth';
import { getStorage, ref as sRef, getBytes } from 'firebase/storage';
import { getFirestore, doc, onSnapshot } from 'firebase/firestore';

import './css/style.css';
import './css/player.css';

import { Midi } from '@tonejs/midi';
import i18n from 'i18next';
import LanguageDetector from 'i18next-browser-languagedetector';
import enTranslation from './locales/en/translation.json';
import deTranslation from './locales/de/translation.json';
import frTranslation from './locales/fr/translation.json';
import esTranslation from './locales/es/translation.json';
import SignalsmithStretchImport from './vendor/signalsmith.js';
import * as VexFlowImport from './vendor/vexflow.js';
import {
  getGuitarChordVoicings,
  parseGuitarChordName,
  renderGuitarChordSvg,
  simplifyGuitarChordLabel
} from './guitarChordVoicings.js';

const app = initializeApp({
  apiKey: "AIzaSyBzQubp8v2ais9gVcf_GCsvdGqs7CduaDk",
  authDomain: "fiveloop-9efa7.firebaseapp.com",
  databaseURL: "https://fiveloop-9efa7-default-rtdb.firebaseio.com",
  projectId: "fiveloop-9efa7",
  storageBucket: "fiveloop-9efa7.appspot.com",
  messagingSenderId: "209102879062",
  appId: "1:209102879062:web:4e5c724e72834cf885fef7"
});

const SignalsmithStretch = SignalsmithStretchImport || window.SignalsmithStretch;
if (SignalsmithStretch && !window.SignalsmithStretch) window.SignalsmithStretch = SignalsmithStretch;
if (SignalsmithStretch && typeof window !== 'undefined' && !SignalsmithStretch.moduleUrl) {
  SignalsmithStretch.moduleUrl = new URL('vendor/signalsmith.js', window.location.href).toString();
}
const VexFlow =
  VexFlowImport?.Renderer ? VexFlowImport :
    VexFlowImport?.default?.Renderer ? VexFlowImport.default :
      VexFlowImport?.Flow?.Renderer ? VexFlowImport.Flow :
        window.Vex?.Renderer ? window.Vex :
          window.Vex?.Flow?.Renderer ? window.Vex.Flow :
            VexFlowImport?.Flow || window.Vex?.Flow || window.Vex || VexFlowImport;

const auth = getAuth(app);
const storage = getStorage(app);
const db = getFirestore(app);

const FOUR_STEM_NAMES = ['bass', 'drums', 'other', 'vocals'];
const SIX_STEM_NAMES = ['bass', 'drums', 'other', 'vocals', 'guitar', 'piano'];
const MIXER_STEM_ORDER = ['drums', 'bass', 'vocals', 'guitar', 'piano', 'other'];
let STEMS = [...FOUR_STEM_NAMES];
let MIXER_ROWS = [...MIXER_STEM_ORDER.filter((name) => STEMS.includes(name)), 'click'];
let DECODABLE_TRACKS = [...STEMS, 'click', 'clickOnset'];
let AUDIO_TRACKS = [...STEMS, 'click'];
const STEM_COLORS = {
  bass: '#9DB4C0',
  drums: '#E6DC87',
  other: '#8A8F98',
  vocals: '#C9A0C0',
  guitar: '#D6A875',
  piano: '#9FA8DA',
  click: '#FFFFFF',
  clickOnset: '#E6DC87'
};
const STEM_TRANSLATION_KEYS = {
  bass: 'stringBass',
  drums: 'stringDrums',
  other: 'stringOther',
  vocals: 'stringVocals',
  guitar: 'stringGuitar',
  piano: 'stringPiano',
  click: 'stringClick',
  clickOnset: 'stringClickOnset'
};
const NOTE_NAMES = ['C', 'C#', 'D', 'D#', 'E', 'F', 'F#', 'G', 'G#', 'A', 'A#', 'B'];
const WAVEFORM_MIN_PX_PER_SECOND = 58;
const WAVEFORM_MAX_BACKING_WIDTH = 32767;
const SIGNALSMITH_LIVE_BLOCK_MS = 320;
const MIDI_NOTE_MERGE_MAXIMUM_BREAK_MS = 250;
const FREE_PREMIUM_LIMIT_SECONDS = 60;

let userId = '';
let midiDownloadPromise = null;
let clickDownloadPromise = null;
let progressiveMidiAvailable = false;
let progressiveClickAvailable = false;
let midiRetryAttempt = 0;
let clickRetryAttempt = 0;
let midiRetryTimer = 0;
let clickRetryTimer = 0;
let clickPairLoaded = false;
let midiFilesLoaded = false;
let stemLoadingInProgress = false;
let dbOpenPromise = null;
let rafId = 0;
let waveformDragRafId = 0;
let waveformZoomTimer = 0;
let waveformScrollRafId = 0;
let lastTranscriptionFrameAt = 0;
let lastMixerMeterFrameAt = 0;
let dragState = null;
let closePlaybackTimer = 0;
let suspendPlaybackTimer = 0;
let playbackRequestId = 0;
let signalsmithFallbackTimer = 0;
let stemLoadingStarted = false;

const state = {
  songId: '',
  songName: '',
  hasPremium: false,
  buffers: {},
  rawBuffers: {},
  waveformPeaks: [],
  duration: 0,
  beats: [],
  chords: [],
  chordHitRects: [],
  openChordVoicingName: '',
  transcriptions: { bass: [], drums: [], other: [], vocals: [], guitar: [], piano: [], click: [] },
  transcriptionPremiumBlocked: false,
  selectedStem: 'bass',
  view: 'piano',
  playing: false,
  playContext: null,
  sources: {},
  gains: {},
  pans: {},
  masterGain: null,
  masterAnalyser: null,
  engine: 'native',
  startedAt: 0,
  visualStartedAt: 0,
  pausedAt: 0,
  speed: 1,
  pitch: 0,
  speedStep: 0.05,
  pitchStep: 1,
  loopOn: false,
  loopState: 0,
  loopStart: 0,
  loopEnd: 0,
  snapToGrid: true,
  ignoreDrumsPitch: false,
  waveformZoom: 1,
  mixerPct: 44,
  fretboardPosition: 5,
  fretboardSpan: 4,
  settings: {},
  stemMode: '4stems',
  piano: null,
  fretboard: null,
  sheet: null
};

function setActiveStemMode(mode) {
  const normalized = String(mode || '').toLowerCase();
  const nextMode = normalized === '6stems' || normalized === 'six' || normalized === 'htdemucs_6s'
    ? '6stems'
    : '4stems';
  if (state.stemMode === nextMode && STEMS.length) {
    ensureMixerSettings(MIXER_ROWS);
    return;
  }

  state.stemMode = nextMode;
  STEMS = nextMode === '6stems' ? [...SIX_STEM_NAMES] : [...FOUR_STEM_NAMES];
  MIXER_ROWS = [...MIXER_STEM_ORDER.filter((name) => STEMS.includes(name)), 'click'];
  DECODABLE_TRACKS = [...STEMS, 'click', 'clickOnset'];
  AUDIO_TRACKS = [...STEMS, 'click'];
  ensureMixerSettings(MIXER_ROWS);
  STEMS.forEach((name) => {
    if (!state.transcriptions[name]) state.transcriptions[name] = [];
  });
  if (!STEMS.includes(state.selectedStem)) {
    state.selectedStem = STEMS.includes('bass') ? 'bass' : STEMS[0];
  }
}

function ensureMixerSettings(names) {
  names.forEach((name) => {
    if (state.settings[name]) return;
    state.settings[name] = {
      volume: 1,
      muted: name === 'click',
      solo: false,
      pan: 0
    };
  });
}

ensureMixerSettings(MIXER_ROWS);

function updateExportMenuForActiveStems() {
  document.querySelectorAll('#exportMenu [data-export-stem]').forEach((button) => {
    const stem = button.dataset.exportStem;
    button.hidden = !STEMS.includes(stem);
  });
}

const els = {};

window.addEventListener('message', handleFiveLoopExtensionMessage);

function handleFiveLoopExtensionMessage(event) {
  if (event.source !== window || event.origin !== window.location.origin) return;
  const message = event.data;
  if (!message ||
      message.source !== 'fiveloop-browser-extension' ||
      message.type !== 'fiveloop-studio-command') return;
  return handleFiveLoopExtensionCommand(message.command);
}

function handleFiveLoopExtensionCommand(command) {
  switch (command) {
    case 'playVideo':
      togglePlayback();
      return true;
    case 'rewindVideo':
      seekBy(-5);
      return true;
    case 'forwardVideo':
      seekBy(5);
      return true;
    case 'repeatVideo':
      toggleLoop();
      return true;
    case 'changeSpeedDown':
      setSpeed(state.speed - state.speedStep);
      return true;
    case 'changeSpeedUp':
      setSpeed(state.speed + state.speedStep);
      return true;
    case 'changePitchDown':
      setPitch(state.pitch - state.pitchStep);
      return true;
    case 'changePitchUp':
      setPitch(state.pitch + state.pitchStep);
      return true;
    default:
      return false;
  }
}

document.addEventListener('DOMContentLoaded', async () => {
  cacheElements();
  await setupi18n();
  bindUI();
  updateExportMenuForActiveStems();
  renderMixer();
  setTranscriptionView(state.view);
});

async function setupi18n() {
  await i18n
    .use(LanguageDetector)
    .init({
      fallbackLng: 'en',
      resources: {
        en: { translation: enTranslation },
      de: { translation: deTranslation },
      fr: { translation: frTranslation },
      es: { translation: esTranslation }
      }
    });

  setText('.txtSettings', i18n.t('stringSettings'));
  setText('.txtGetPremium', i18n.t('stringGetPremium'));
  setText('.txtGetPremiumDetailInPlayer', i18n.t('stringGetPremiumDetailInPlayer'));
  setText('.txtPremiumInfo', i18n.t('stringPremiumInfo'));
  setText('.txtKey', i18n.t('stringKey'));
  setText('.txtBpm', i18n.t('stringBpm'));
  setText('.txtSpeedSteps', i18n.t('stringSpeedSteps'));
  setText('.txtPitchSteps', i18n.t('stringPitchSteps'));
  setText('.txtOneSemitone', i18n.t('stringOneSemitone'));
  setText('.txtTenCents', i18n.t('stringTenCents'));
  setText('.txtSnapToBeatgridInfo', i18n.t('stringSnapToBeatgridInfo'));
  setText('.txtIgnoreDrumsForPitchSteps', i18n.t('stringIgnoreDrumsForPitchSteps'));
  setText('.txtExportBass', i18n.t('stringExportBass'));
  setText('.txtExportDrums', i18n.t('stringExportDrums'));
  setText('.txtExportOther', i18n.t('stringExportOther'));
  setText('.txtExportVocals', i18n.t('stringExportVocals'));
  setText('.txtExportGuitar', i18n.t('stringExportGuitar'));
  setText('.txtExportPiano', i18n.t('stringExportPiano'));
  setText('.txtLoadingStemsEllipsis', i18n.t('stringLoadingStemsEllipsis'));
  setText('.txtMixer', i18n.t('stringMixer'));
  setText('.txtTranscription', i18n.t('stringTranscription'));
  setText('.txtSheet', i18n.t('stringSheet'));
  setText('.txtPiano', i18n.t('stringPiano'));
  setText('.txtFretboard', i18n.t('stringFretboard'));
  setText('.txtPitch', i18n.t('stringPitch'));
  setText('.txtSpeed', i18n.t('stringSpeed'));
  if (els.songMoreBtn) els.songMoreBtn.title = i18n.t('stringMore');
  if (els.waveformZoomSlider?.closest('label')) els.waveformZoomSlider.closest('label').title = i18n.t('stringWaveformZoom');
  if (els.guitarChordClose) els.guitarChordClose.setAttribute('aria-label', i18n.t('stringClose'));
  if (els.splitHandle) els.splitHandle.title = i18n.t('stringDragToResize');
  if (els.settingsBtn) els.settingsBtn.title = i18n.t('stringSettings');
  if (els.rewindBtn) els.rewindBtn.title = i18n.t('stringBack');
  if (els.playBtn) els.playBtn.title = i18n.t('stringPlay');
  if (els.forwardBtn) els.forwardBtn.title = i18n.t('stringForward');
  if (els.loopBtn) els.loopBtn.title = i18n.t('stringLoop');
}

function getStemLabel(stem) {
  const key = STEM_TRANSLATION_KEYS[stem];
  return key ? i18n.t(key) : stem;
}

function cacheElements() {
  [
    'settingsModal', 'premiumModal', 'closeSettings', 'closePremium', 'goSubscribeBtn',
    'speedStepSelect', 'pitchStepSelect', 'snapToGridToggle', 'ignoreDrumsPitchToggle',
    'songMoreBtn', 'exportMenu', 'waveformCanvas', 'waveformPlayhead', 'waveformWrap', 'waveformScroller',
    'waveformTimeline', 'waveformZoomSlider', 'waveformLoading',
    'guitarChordPanel', 'guitarChordTitle', 'guitarChordGrid', 'guitarChordEmpty', 'guitarChordClose',
    'durationLabel', 'currentTimeLabel', 'totalTimeLabel', 'mixerMeta',
    'mixerRows', 'studioSplitRow', 'splitHandle', 'transcriptionPanel',
    'transcriptionStemDot', 'transcriptionStemName', 'transcriptionMeta',
    'sheetView', 'pianoView', 'fretboardView', 'settingsBtn',
    'pitchDownBtn', 'pitchUpBtn', 'pitchLabel', 'speedDownBtn', 'speedUpBtn',
    'speedLabel', 'rewindBtn', 'playBtn', 'forwardBtn', 'loopBtn'
  ].forEach((id) => {
    els[id] = document.getElementById(id);
  });
}

function bindUI() {
  document.addEventListener('click', (event) => {
    if (!event.target.closest('.studio-more')) {
      els.exportMenu?.classList.remove('dropdown4show');
    }
  });

  els.songMoreBtn?.addEventListener('click', () => els.exportMenu?.classList.toggle('dropdown4show'));
  els.settingsBtn?.addEventListener('click', () => showModal(els.settingsModal));
  els.closeSettings?.addEventListener('click', () => hideModal(els.settingsModal));
  els.closePremium?.addEventListener('click', () => hideModal(els.premiumModal));
  els.guitarChordClose?.addEventListener('click', hideGuitarChordPanel);
  els.goSubscribeBtn?.addEventListener('click', () => { window.location.href = 'subscribe.html'; });

  window.addEventListener('click', (event) => {
    if (event.target === els.settingsModal) hideModal(els.settingsModal);
    if (event.target === els.premiumModal) hideModal(els.premiumModal);
  });

  els.speedStepSelect?.addEventListener('change', (event) => {
    state.speedStep = Number(event.target.value) || 0.05;
  });
  els.pitchStepSelect?.addEventListener('change', (event) => {
    state.pitchStep = Number(event.target.value) || 1;
  });
  els.snapToGridToggle?.addEventListener('change', (event) => {
    state.snapToGrid = !!event.target.checked;
  });
  if (els.snapToGridToggle) state.snapToGrid = !!els.snapToGridToggle.checked;
  els.ignoreDrumsPitchToggle?.addEventListener('change', (event) => {
    state.ignoreDrumsPitch = !!event.target.checked;
    if (state.playing) restartAt(getPosition());
  });
  els.waveformZoomSlider?.addEventListener('input', (event) => {
    state.waveformZoom = Math.max(0, Math.min(1, (Number(event.target.value) || 0) / 100));
    scheduleWaveformZoomUpdate();
    syncWaveformScrollToPlayback(true);
  });

  els.playBtn?.addEventListener('click', togglePlayback);
  els.rewindBtn?.addEventListener('click', () => seekBy(-5));
  els.forwardBtn?.addEventListener('click', () => seekBy(5));
  els.speedDownBtn?.addEventListener('click', () => setSpeed(state.speed - state.speedStep));
  els.speedUpBtn?.addEventListener('click', () => setSpeed(state.speed + state.speedStep));
  els.pitchDownBtn?.addEventListener('click', () => setPitch(state.pitch - state.pitchStep));
  els.pitchUpBtn?.addEventListener('click', () => setPitch(state.pitch + state.pitchStep));
  els.loopBtn?.addEventListener('click', toggleLoop);

  document.querySelectorAll('.tab').forEach((tab) => {
    tab.addEventListener('click', () => setTranscriptionView(tab.dataset.view));
  });
  els.fretboardView?.addEventListener('click', (event) => {
    const button = event.target.closest('[data-fret-action]');
    if (button) handleFretboardControl(button.dataset.fretAction);
  });

  els.waveformCanvas?.addEventListener('pointerdown', onWaveformPointerDown);
  els.waveformCanvas?.addEventListener('pointermove', onWaveformPointerMove);
  els.waveformCanvas?.addEventListener('pointerleave', () => { if (!dragState) els.waveformCanvas.style.cursor = 'crosshair'; });
  els.waveformScroller?.addEventListener('scroll', onWaveformScroll, { passive: true });
  installHorizontalOverscrollGuard(els.waveformScroller);
  window.addEventListener('resize', () => {
    drawWaveform();
    renderTranscription();
  });

  els.splitHandle?.addEventListener('pointerdown', startSplitDrag);

  bindExport('exportBass', 'bass');
  bindExport('exportDrums', 'drums');
  bindExport('exportOther', 'other');
  bindExport('exportVocals', 'vocals');
  bindExport('exportGuitar', 'guitar');
  bindExport('exportPiano', 'piano');
  setupSettingsControls();
}

function setupSettingsControls() {
  [els.speedStepSelect, els.pitchStepSelect].forEach((select) => {
    if (!select || select.dataset.segmentedReady === '1') return;
    select.dataset.segmentedReady = '1';
    const control = document.createElement('div');
    control.className = 'fl-segmented-control';

    const sync = () => {
      Array.from(control.children).forEach((button) => {
        button.classList.toggle('is-active', button.dataset.value === select.value);
      });
    };

    Array.from(select.options).forEach((option) => {
      const button = document.createElement('button');
      button.type = 'button';
      button.className = 'fl-segmented-button';
      button.dataset.value = option.value;
      button.textContent = option.textContent;
      button.addEventListener('click', (event) => {
        event.preventDefault();
        select.value = option.value;
        sync();
        select.dispatchEvent(new Event('change', { bubbles: true }));
      });
      control.appendChild(button);
    });

    select.addEventListener('change', sync);
    select.insertAdjacentElement('afterend', control);
    sync();
  });
}

function bindExport(id, name) {
  const el = document.getElementById(id);
  el?.addEventListener('click', () => exportStem(name));
}

function showModal(el) {
  if (el) el.style.display = 'block';
}

function hideModal(el) {
  if (el) el.style.display = 'none';
}

function setText(selector, text) {
  document.querySelectorAll(selector).forEach((el) => { el.textContent = text; });
}

onAuthStateChanged(auth, (user) => {
  state.songId = getUrlParameter('songid') || '';
  if (user) {
    if (!user.emailVerified) {
      window.location.replace('index.html');
      return;
    }
    const navbarUsername = document.getElementById('navbarUsername');
    if (navbarUsername) navbarUsername.textContent = user.email;
    userId = user.uid;
    getSongData();
    userHasPremiumCustomClaim().then((hasPremium) => {
      state.hasPremium = hasPremium;
      window.ccrinp = !hasPremium && !isDemoSong(state.songId);
      if (hasPremium) {
        const premiumTag = document.getElementById('navbarPremiumTag');
        if (premiumTag) premiumTag.textContent = 'Premium';
      } else {
        document.getElementById('premiumInfoBtn')?.classList.remove('hidedisplay');
        if (isDemoSong(state.songId)) window.ccrinp = false;
      }
      document.getElementById('navbarUsersettings')?.classList.remove('hide');
      drawWaveform();
      renderTranscription();
    });
  } else {
    userId = '';
    if (!isDemoSong(state.songId)) {
      window.location.replace('login.html');
      return;
    }
    window.ccrinp = false;
    getSongData();
  }
});

async function userHasPremiumCustomClaim() {
  await auth.currentUser.getIdToken(true);
  const decodedToken = await auth.currentUser.getIdTokenResult();
  const stripeRole = decodedToken.claims.stripeRole;
  const lemonSqueezyRole = decodedToken.claims.lemonSqueezyRole;
  const revenueCatEntitlements = decodedToken.claims.revenueCatEntitlements;
  return stripeRole === 'premium' ||
    lemonSqueezyRole === 'premium' ||
    (revenueCatEntitlements && revenueCatEntitlements.includes('Premium'));
}

document.addEventListener('click', (event) => {
  if (event.target.closest('#logoutBtn')) {
    signOut(auth).catch((error) => alert(error));
  }
});

function getSongData() {
  const songId = state.songId;
  subscribeSongTitle(songId);

  onSnapshot(songDocRef(db, songId, userId), (snapshot) => {
    if (!snapshot.exists()) {
      setLoadingText(i18n.t('stringWaitingForSongFiles'));
      return;
    }
    const data = snapshot.data() || {};
    setActiveStemMode(data.stems);
    updateExportMenuForActiveStems();
    if (!stemLoadingStarted) {
      stemLoadingStarted = true;
      loadCachedOrRemoteStems(songId);
    }
    if (typeof data.chords !== 'undefined') setChords(data.chords);
    if (typeof data.beatsTimesDownbeat !== 'undefined') setBeats(data.beatsTimesDownbeat);
    else if (typeof data.beatsTimes !== 'undefined') setBeats(data.beatsTimes);
    progressiveClickAvailable = data.hasClick === true;
    progressiveMidiAvailable = data.hasMidi === true;
    if (progressiveClickAvailable) ensureClickTracksAvailable();
    if (progressiveMidiAvailable) ensureMidisAvailable();
    if (typeof data.tempo !== 'undefined') {
      state.tempo = Math.round(data.tempo);
      setText('.songTempoLbl', state.tempo);
    }
    if (typeof data.key !== 'undefined') {
      state.key = data.key;
      setText('.songKeyLbl', data.key);
    }
  });
}

function subscribeSongTitle(songId) {
  const ref = !isDemoSong(songId) && userId
    ? doc(db, `users/${userId}/songs`, songId)
    : doc(db, 'demoSongs', songId);
  onSnapshot(ref, (snapshot) => {
    const songName = snapshot.data()?.songName;
    if (songName && state.songName !== songName) {
      state.songName = songName;
      setText('.tracktitelheader', songName);
    }
  });
}

async function loadCachedOrRemoteStems(songId) {
  try {
    const dbi = await openSongDatabase();
    const cached = await idbGet(dbi, 'audiofiles', songId);
    if (cached) {
      if (!STEMS.every((name) => cached[name])) {
        downloadStems();
        return;
      }
      await applyRawStemBuffers(cached);
      if (!cached.click || !cached.clickOnset) ensureClickTracksAvailable();
      return;
    }
  } catch (error) {
    console.warn('IndexedDB read failed, downloading stems', error);
  }
  downloadStems();
}

async function applyRawStemBuffers(record) {
  STEMS.forEach((name) => {
    if (record[name]) {
      state.rawBuffers[name] = record[name];
      window[`arrayBuffer${capitalize(name)}`] = record[name];
    }
  });
  if (record.click) state.rawBuffers.click = record.click;
  if (record.clickOnset) state.rawBuffers.clickOnset = record.clickOnset;
  if (record.click && record.clickOnset) clickPairLoaded = true;
  await decodeAllAvailableBuffers();
}

async function decodeAllAvailableBuffers() {
  const names = DECODABLE_TRACKS.filter((name) => state.rawBuffers[name] && !state.buffers[name]);
  if (!names.length) {
    renderReadyState();
    return;
  }

  for (const name of names) {
    try {
      state.buffers[name] = await decodeAudioBuffer(state.rawBuffers[name]);
    } catch (error) {
      console.warn(`Failed to decode ${name}`, error);
    }
  }
  renderReadyState();
}

function renderReadyState() {
  const availableStems = STEMS.filter((name) => state.buffers[name]);
  if (!availableStems.length) return;
  state.duration = availableStems.reduce((duration, name) => Math.max(duration, state.buffers[name].duration || 0), 0);
  if (!state.loopEnd) state.loopEnd = state.duration;
  computeWaveformPeaks();
  els.waveformLoading?.classList.add('is-hidden');
  updateTimeLabels();
  renderMixer();
  renderTranscription();
  drawWaveform();
}

async function decodeAudioBuffer(arrayBuffer) {
  const AudioContextCtor = window.AudioContext || window.webkitAudioContext;
  const ctx = new AudioContextCtor();
  const copy = arrayBuffer.slice ? arrayBuffer.slice(0) : arrayBuffer;
  try {
    return await ctx.decodeAudioData(copy);
  } finally {
    if (ctx.state !== 'closed') {
      try { await ctx.close(); } catch (error) {}
    }
  }
}

async function downloadStems() {
  const songId = state.songId;
  stemLoadingInProgress = true;
  setLoadingText(i18n.t('stringLoadingStems'));
  try {
    const entries = await Promise.all(STEMS.map(async (stem) => [
      stem,
      await getBytes(sRef(storage, storagePath(songId, `${stem}.mp3`)))
    ]));
    const record = { id: songId, name: state.songName, stems: state.stemMode };
    entries.forEach(([stem, bytes]) => {
      state.rawBuffers[stem] = bytes;
      window[`arrayBuffer${capitalize(stem)}`] = bytes;
      record[stem] = bytes;
    });

    setLoadingText(i18n.t('stringLoadingClick'));
    const clickLoaded = await downloadClickTracks(songId);
    if (clickLoaded) {
      record.click = state.rawBuffers.click;
      record.clickOnset = state.rawBuffers.clickOnset;
    }

    stemLoadingInProgress = false;
    await storeAudioRecord(songId, record);
    await decodeAllAvailableBuffers();
  } catch (error) {
    stemLoadingInProgress = false;
    setLoadingText(i18n.t('stringStemLoadingFailed'));
    alert(error);
  }
}

async function downloadClickTracks(songId) {
  const [click, clickOnset] = await Promise.all([
    downloadOptionalStorageBytes(storagePath(songId, 'click.mp3')),
    downloadOptionalStorageBytes(storagePath(songId, 'clickonset.mp3'))
  ]);
  const playableClick = click || clickOnset;
  const onsetClick = clickOnset || click;
  if (!playableClick) {
    console.warn('Click tracks unavailable');
    return false;
  }
  state.rawBuffers.click = playableClick;
  state.rawBuffers.clickOnset = onsetClick;
  clickPairLoaded = Boolean(click && clickOnset);
  window.arrayBufferClick = playableClick;
  window.arrayBufferClickOnset = onsetClick;
  return progressiveClickAvailable ? clickPairLoaded : true;
}

async function downloadOptionalStorageBytes(path) {
  try {
    return await getBytes(sRef(storage, path));
  } catch (error) {
    if (error.code !== 'storage/object-not-found') console.warn(`Storage file unavailable: ${path}`, error);
    return null;
  }
}

async function downloadClickTracksAfterStemsAreStored() {
  if (stemLoadingInProgress || !state.songId) return false;
  if (state.rawBuffers.click && (!progressiveClickAvailable || clickPairLoaded)) return true;
  const ok = await downloadClickTracks(state.songId);
  if (!ok) return false;
  try {
    const dbi = await openSongDatabase();
    const record = await idbGet(dbi, 'audiofiles', state.songId);
    if (record) {
      record.click = state.rawBuffers.click;
      record.clickOnset = state.rawBuffers.clickOnset;
      await idbPut(dbi, 'audiofiles', record);
    }
  } catch (error) {
    console.warn('Failed to cache click tracks', error);
  }
  await decodeAllAvailableBuffers();
  renderMixer();
  return true;
}

async function ensureClickTracksAvailable() {
  if (state.rawBuffers.click && (!progressiveClickAvailable || clickPairLoaded)) {
    clearProgressiveRetry('click');
    return true;
  }
  if (clickDownloadPromise) return clickDownloadPromise;
  clickDownloadPromise = downloadClickTracksAfterStemsAreStored()
    .then((loaded) => {
      if (loaded) clearProgressiveRetry('click');
      else if (progressiveClickAvailable) scheduleProgressiveRetry('click');
      return loaded;
    })
    .catch((error) => {
      console.warn('Failed to load progressive click tracks', error);
      if (progressiveClickAvailable) scheduleProgressiveRetry('click');
      return false;
    })
    .finally(() => { clickDownloadPromise = null; });
  return clickDownloadPromise;
}

async function storeAudioRecord(songId, record) {
  try {
    const dbi = await openSongDatabase();
    await idbPut(dbi, 'audiofiles', record);
    const total = Object.keys(record).reduce((sum, key) => {
      return record[key] instanceof ArrayBuffer ? sum + record[key].byteLength : sum;
    }, 0);
    await idbPut(dbi, 'sizeInBytes', { id: songId, total });
  } catch (error) {
    console.warn('IndexedDB write failed', error);
  }
}

function openSongDatabase() {
  if (dbOpenPromise) return dbOpenPromise;
  dbOpenPromise = new Promise((resolve, reject) => {
    const request = indexedDB.open('songs', 1);
    request.onerror = () => reject(request.error);
    request.onupgradeneeded = () => {
      const dbi = request.result;
      if (!dbi.objectStoreNames.contains('audiofiles')) dbi.createObjectStore('audiofiles', { keyPath: 'id' });
      if (!dbi.objectStoreNames.contains('sizeInBytes')) dbi.createObjectStore('sizeInBytes', { keyPath: 'id' });
    };
    request.onsuccess = () => resolve(request.result);
  });
  return dbOpenPromise;
}

function idbGet(dbi, storeName, id) {
  return new Promise((resolve, reject) => {
    const tx = dbi.transaction(storeName, 'readonly');
    const request = tx.objectStore(storeName).get(id);
    request.onerror = () => reject(request.error);
    request.onsuccess = () => resolve(request.result);
  });
}

function idbPut(dbi, storeName, value) {
  return new Promise((resolve, reject) => {
    const tx = dbi.transaction(storeName, 'readwrite');
    const request = tx.objectStore(storeName).put(value);
    request.onerror = () => reject(request.error);
    tx.oncomplete = () => resolve();
    tx.onerror = () => reject(tx.error);
  });
}

async function ensureMidisAvailable() {
  if (midiFilesLoaded) return true;
  if (midiDownloadPromise) return midiDownloadPromise;
  midiDownloadPromise = downloadMidis()
    .then((loaded) => {
      if (loaded) {
        midiFilesLoaded = true;
        clearProgressiveRetry('midi');
      }
      else if (progressiveMidiAvailable) scheduleProgressiveRetry('midi');
      return loaded;
    })
    .catch((error) => {
      console.warn('Failed to load progressive MIDI files', error);
      if (progressiveMidiAvailable) scheduleProgressiveRetry('midi');
      return false;
    })
    .finally(() => { midiDownloadPromise = null; });
  return midiDownloadPromise;
}

async function downloadMidis() {
  const songId = state.songId;
  const loaded = await Promise.all(STEMS
    .filter((stem) => stem !== 'drums')
    .map((stem) => loadMidiForStem(songId, stem)));
  renderTranscription();
  return loaded.every(Boolean);
}

async function loadMidiForStem(songId, stem) {
  try {
    const arrayBuffer = await getBytes(sRef(storage, storagePath(songId, `${stem}.mid`)));
    const midi = new Midi(arrayBuffer);
    const track = midi.tracks[0];
    const notes = (track?.notes || []).map((note) => ({
      startTime: note.time,
      endTime: note.time + note.duration,
      midiPitch: note.midi,
      amplitude: note.velocity,
      noteName: note.name
    })).sort((a, b) => a.startTime - b.startTime);
    const mergedNotes = mergeNearbyRepeatedNotes(notes);
    state.transcriptions[stem] = stem === 'bass' ? removeBassOctaveDuplicates(mergedNotes) : mergedNotes;
    return true;
  } catch (error) {
    if (error.code !== 'storage/object-not-found') console.warn(`Failed to load ${stem}.mid`, error);
    return false;
  }
}

function scheduleProgressiveRetry(kind) {
  const isMidi = kind === 'midi';
  const available = isMidi ? progressiveMidiAvailable : progressiveClickAvailable;
  const timer = isMidi ? midiRetryTimer : clickRetryTimer;
  const attempt = isMidi ? midiRetryAttempt : clickRetryAttempt;
  if (!available || timer || attempt >= 6) return;
  const delay = Math.min(1000 * (2 ** attempt), 16000);
  const retry = window.setTimeout(() => {
    if (isMidi) {
      midiRetryTimer = 0;
      midiRetryAttempt += 1;
      ensureMidisAvailable();
    } else {
      clickRetryTimer = 0;
      clickRetryAttempt += 1;
      ensureClickTracksAvailable();
    }
  }, delay);
  if (isMidi) midiRetryTimer = retry;
  else clickRetryTimer = retry;
}

function clearProgressiveRetry(kind) {
  if (kind === 'midi') {
    if (midiRetryTimer) window.clearTimeout(midiRetryTimer);
    midiRetryTimer = 0;
    midiRetryAttempt = 0;
  } else {
    if (clickRetryTimer) window.clearTimeout(clickRetryTimer);
    clickRetryTimer = 0;
    clickRetryAttempt = 0;
  }
}

function mergeNearbyRepeatedNotes(notes) {
  const maximumBreak = MIDI_NOTE_MERGE_MAXIMUM_BREAK_MS / 1000;
  const byPitch = new Map();
  (notes || []).forEach((note) => {
    const pitch = Math.round(Number(note.midiPitch));
    if (!Number.isFinite(pitch)) return;
    if (!byPitch.has(pitch)) byPitch.set(pitch, []);
    byPitch.get(pitch).push(note);
  });

  const merged = [];
  byPitch.forEach((pitchNotes) => {
    const result = [];
    pitchNotes
      .slice()
      .sort((a, b) => (a.startTime || 0) - (b.startTime || 0))
      .forEach((note) => {
        const start = Number(note.startTime) || 0;
        const end = Math.max(start + 0.04, Number(note.endTime) || start + 0.04);
        const previous = result[result.length - 1];
        if (!previous) {
          result.push({ ...note, startTime: start, endTime: end });
          return;
        }

        const previousEnd = Number(previous.endTime) || previous.startTime || 0;
        const breakLength = start - previousEnd;
        if (breakLength >= -0.001 && breakLength <= maximumBreak) {
          previous.endTime = Math.max(previousEnd, end);
          previous.amplitude = Math.max(Number(previous.amplitude) || 0, Number(note.amplitude) || 0);
        } else {
          result.push({ ...note, startTime: start, endTime: end });
        }
      });
    merged.push(...result);
  });

  return merged.sort((a, b) => {
    const startA = Number(a.startTime) || 0;
    const startB = Number(b.startTime) || 0;
    return startA === startB
      ? (Number(a.midiPitch) || 0) - (Number(b.midiPitch) || 0)
      : startA - startB;
  });
}

function removeBassOctaveDuplicates(notes) {
  const byStart = new Map();
  notes.forEach((note) => {
    const key = note.startTime.toFixed(4);
    const existing = byStart.get(key);
    if (!existing || note.midiPitch < existing.midiPitch) byStart.set(key, note);
  });
  return Array.from(byStart.values()).sort((a, b) => a.startTime - b.startTime);
}

function setBeats(raw) {
  try {
    const parsed = typeof raw === 'string' ? JSON.parse(raw) : raw;
    state.beats = (Array.isArray(parsed) ? parsed : []).map((item, index) => {
      if (typeof item === 'number') return { time: item, beat: (index % 4) + 1, isDownbeat: false };
      const time = Number(item.time ?? item[0] ?? item);
      const beatNumber = Number(item.beat ?? item.beatNumber ?? item[1] ?? ((index % 4) + 1));
      const hasExplicitDownbeat = Object.prototype.hasOwnProperty.call(item, 'isDownbeat') ||
        Object.prototype.hasOwnProperty.call(item, 'downbeat') ||
        Object.prototype.hasOwnProperty.call(item, 'beat') ||
        Object.prototype.hasOwnProperty.call(item, 'beatNumber') ||
        Array.isArray(item);
      return {
        time,
        beat: beatNumber,
        isDownbeat: hasExplicitDownbeat
          ? !!(item.isDownbeat || item.downbeat || beatNumber === 1)
          : false
      };
    }).filter((beat) => isFinite(beat.time));
    drawWaveform();
    if (!state.rawBuffers.click && !stemLoadingInProgress) ensureClickTracksAvailable();
  } catch (error) {
    console.warn('Invalid beat data', error);
  }
}

function setChords(raw) {
  try {
    const parsed = typeof raw === 'string' ? JSON.parse(raw) : raw;
    const entries = (Array.isArray(parsed) ? parsed : []).map((item, index, list) => {
      const chord = item.chord ?? item.name ?? item[0];
      const start = Number(item.start ?? item.time ?? item[1] ?? 0);
      const next = list[index + 1];
      const end = Number(item.end ?? next?.start ?? next?.time ?? next?.[1] ?? state.duration);
      return { chord, start, end: Math.max(start + 0.05, end || start + 0.05) };
    }).filter((item) => item.chord && isFinite(item.start));
    state.chords = entries;
    drawWaveform();
    renderTranscription();
  } catch (error) {
    console.warn('Invalid chord data', error);
  }
}

function computeWaveformPeaks() {
  const duration = state.duration || Math.max(0, ...STEMS.map((name) => state.buffers[name]?.duration || 0));
  if (!duration) return;
  const dpr = Math.max(1, Math.min(2, window.devicePixelRatio || 1));
  const width = Math.min(60000, Math.max(2400, Math.ceil(duration * WAVEFORM_MIN_PX_PER_SECOND * dpr)));
  const peaks = [];
  for (let x = 0; x < width; x += 1) {
    let peak = 0;
    const startTime = (x / width) * duration;
    const endTime = ((x + 1) / width) * duration;
    STEMS.forEach((name) => {
      const buffer = state.buffers[name];
      if (!buffer) return;
      const startFrame = Math.max(0, Math.floor(startTime * buffer.sampleRate));
      const endFrame = Math.min(buffer.length, Math.max(startFrame + 1, Math.ceil(endTime * buffer.sampleRate)));
      const scanStep = Math.max(1, Math.floor((endFrame - startFrame) / 80));
      for (let idx = startFrame; idx < endFrame; idx += scanStep) {
        for (let c = 0; c < buffer.numberOfChannels; c += 1) {
          peak = Math.max(peak, Math.abs(buffer.getChannelData(c)[idx] || 0));
        }
      }
    });
    peaks.push(peak);
  }
  state.waveformPeaks = peaks;
}

async function togglePlayback() {
  if (state.playing) {
    pausePlayback();
  } else {
    startPlayback(state.pausedAt || state.loopStart || 0);
  }
}

async function startPlayback(offset) {
  if (!STEMS.some((name) => state.buffers[name])) {
    console.warn('FiveLoop playback ignored: no decoded stem buffers');
    return;
  }
  const requestId = ++playbackRequestId;
  const startOffset = clampOffset(offset);
  state.playing = true;
  state.pausedAt = startOffset;
  updateTransport();
  updateWaveformPlayhead();
  updateTimeLabels();
  if (closePlaybackTimer) {
    clearTimeout(closePlaybackTimer);
    closePlaybackTimer = 0;
  }
  if (suspendPlaybackTimer) {
    clearTimeout(suspendPlaybackTimer);
    suspendPlaybackTimer = 0;
  }
  const AudioContextCtor = window.AudioContext || window.webkitAudioContext;
  const ctx = state.playContext && state.playContext.state !== 'closed'
    ? state.playContext
    : new AudioContextCtor();
  state.playContext = ctx;
  state.engine = 'native';
  state.startedAt = ctx.currentTime;
  state.visualStartedAt = performance.now() / 1000;
  startAnimationLoop();
  updateTranscriptionPlayback();
  if (ctx.state === 'suspended' || ctx.state === 'interrupted') {
    try {
      await ctx.resume();
    } catch (error) {
      console.warn('Failed to resume audio context', error);
    }
    if (requestId !== playbackRequestId || !state.playing || state.playContext !== ctx) return;
  }
  await afterNextPaint();
  if (requestId !== playbackRequestId || !state.playing || state.playContext !== ctx) return;
  stopPlaybackGraph();
  state.sources = {};
  state.gains = {};
  state.pans = {};
  createMasterBus(ctx, 0.08);

  const useTransform = Math.abs(state.speed - 1) > 0.0001 || Math.abs(state.pitch) > 0.0001;
  const started = useTransform && SignalsmithStretch
    ? await startSignalsmithPlayback(ctx, startOffset, requestId).catch((error) => {
      if (!isPlaybackRequestCurrent(requestId, ctx)) return null;
      console.warn('Signalsmith playback failed, falling back to native', error);
      return false;
    })
    : false;

  if (!isPlaybackRequestCurrent(requestId, ctx)) return;
  if (!started) startNativePlayback(ctx, startOffset, requestId);
  else scheduleSignalsmithSilenceFallback(ctx, requestId);
  if (!isPlaybackRequestCurrent(requestId, ctx)) return;

  updateTransport();
  startAnimationLoop();
}

function isPlaybackRequestCurrent(requestId, ctx) {
  return requestId === playbackRequestId && state.playing && state.playContext === ctx;
}

function createMasterBus(ctx, rampSeconds = 0.08) {
  state.masterGain = ctx.createGain();
  state.masterGain.gain.setValueAtTime(0.0001, ctx.currentTime);
  state.masterGain.gain.exponentialRampToValueAtTime(1, ctx.currentTime + rampSeconds);
  state.masterAnalyser = ctx.createAnalyser();
  state.masterAnalyser.fftSize = 2048;
  state.masterGain.connect(state.masterAnalyser);
  state.masterAnalyser.connect(ctx.destination);
}

function startNativePlayback(ctx, offset, requestId = playbackRequestId) {
  if (!isPlaybackRequestCurrent(requestId, ctx) || !state.masterGain) return false;
  const when = ctx.currentTime + 0.02;
  AUDIO_TRACKS.forEach((name) => {
    if (!isPlaybackRequestCurrent(requestId, ctx) || !state.masterGain) return;
    const buffer = state.buffers[name];
    if (!buffer) return;
    const source = ctx.createBufferSource();
    const gain = ctx.createGain();
    const pan = ctx.createStereoPanner ? ctx.createStereoPanner() : null;
    source.buffer = buffer;
    source.loop = state.loopOn;
    source.loopStart = getActiveLoopStart();
    source.loopEnd = Math.min(getActiveLoopEnd(), buffer.duration);
    source.playbackRate.value = state.speed;
    if (!connectTrack(name, source, gain, pan)) return;
    const startAt = Math.min(offset, Math.max(0, buffer.duration - 0.001));
    try { source.start(when, startAt); } catch (error) { console.warn(`Failed to start ${name}`, error); }
  });
  if (!isPlaybackRequestCurrent(requestId, ctx)) return false;
  state.startedAt = when;
  state.engine = 'native';
  applyMixer();
  return true;
}

async function startSignalsmithPlayback(ctx, offset, requestId) {
  const when = ctx.currentTime + 0.16;
  const start = state.loopOn ? getActiveLoopStart() : 0;
  const end = state.loopOn ? getActiveLoopEnd() : state.duration;
  const loopLength = Math.max(0.05, end - start);
  const localOffset = Math.max(0, Math.min(state.loopOn ? offset - start : offset, Math.max(0, loopLength - 0.001)));
  const jobs = AUDIO_TRACKS
    .filter((name) => state.buffers[name])
    .map((name) => ({ name, buffer: copyBufferRegion(ctx, state.buffers[name], start, end) }));
  if (!jobs.length) return false;

  const activeEntries = [];
  try {
    for (const job of jobs) {
      if (!isPlaybackRequestCurrent(requestId, ctx) || !state.masterGain) return null;
      const gain = ctx.createGain();
      const pan = ctx.createStereoPanner ? ctx.createStereoPanner() : null;
      const stretch = await createSignalsmithNode(ctx, job.buffer);
      if (!isPlaybackRequestCurrent(requestId, ctx) || !state.masterGain) {
        cleanupPlaybackEntries([{ name: job.name, source: stretch, gain, pan }]);
        cleanupPlaybackEntries(activeEntries);
        return null;
      }
      activeEntries.push({ name: job.name, source: stretch, gain, pan });
      if (!connectTrack(job.name, stretch, gain, pan)) {
        cleanupPlaybackEntries(activeEntries);
        return false;
      }
      const input = Math.min(localOffset, Math.max(0, job.buffer.duration - 0.001));
      await stretch.schedule({
        active: true,
        outputTime: when,
        output: when,
        input,
        rate: Math.max(0.05, state.speed),
        semitones: getTrackPitch(job.name),
        loopStart: 0,
        loopEnd: job.buffer.duration
      });
      if (!isPlaybackRequestCurrent(requestId, ctx)) {
        cleanupPlaybackEntries(activeEntries);
        return null;
      }
    }
  } catch (error) {
    cleanupPlaybackEntries(activeEntries);
    throw error;
  }

  if (!isPlaybackRequestCurrent(requestId, ctx)) {
    cleanupPlaybackEntries(activeEntries);
    return null;
  }
  state.startedAt = when;
  state.engine = 'signalsmith';
  applyMixer();
  return true;
}

function cleanupPlaybackEntries(entries) {
  entries.forEach(({ name, source, gain, pan }) => {
    try { if (source && typeof source.stop === 'function') source.stop(0); } catch (error) {}
    try { if (source) source.disconnect(); } catch (error) {}
    try { if (gain) gain.disconnect(); } catch (error) {}
    try { if (pan) pan.disconnect(); } catch (error) {}
    if (name && state.sources[name] === source) delete state.sources[name];
    if (name && state.gains[name] === gain) delete state.gains[name];
    if (name && state.pans[name] === pan) delete state.pans[name];
  });
}

function scheduleSignalsmithSilenceFallback(ctx, requestId) {
  if (signalsmithFallbackTimer) window.clearTimeout(signalsmithFallbackTimer);
  signalsmithFallbackTimer = window.setTimeout(() => {
    signalsmithFallbackTimer = 0;
    if (requestId !== playbackRequestId || !state.playing || state.playContext !== ctx || state.engine !== 'signalsmith') return;
    const expected = getExpectedAudibleLevel(getPosition());
    if (expected < 0.01) return;
    const rms = readMasterRms();
    if (rms === null || rms > 0.00002) return;
    const offset = getPosition();
    console.warn('Signalsmith produced silence; falling back to native playback', { expected, rms, offset });
    stopPlaybackGraph();
    state.sources = {};
    state.gains = {};
    state.pans = {};
    createMasterBus(ctx, 0.03);
    startNativePlayback(ctx, offset, requestId);
    updateTransport();
    startAnimationLoop();
  }, 950);
}

function getExpectedAudibleLevel(position) {
  return AUDIO_TRACKS.reduce((level, name) => {
    return Math.max(level, estimateStemLevel(name, position) * getMixerEffectiveVolume(name));
  }, 0);
}

function readMasterRms() {
  const analyser = state.masterAnalyser;
  if (!analyser || typeof analyser.getFloatTimeDomainData !== 'function') return null;
  const data = new Float32Array(analyser.fftSize);
  try {
    analyser.getFloatTimeDomainData(data);
  } catch (error) {
    return null;
  }
  let sum = 0;
  for (let index = 0; index < data.length; index += 1) sum += data[index] * data[index];
  return Math.sqrt(sum / Math.max(1, data.length));
}

function createSignalsmithNode(ctx, buffer) {
  return SignalsmithStretch(ctx, {
    numberOfInputs: 0,
    numberOfOutputs: 1,
    outputChannelCount: [2]
  }).then((stretch) => {
    return stretch.configure({
      preset: 'default',
      blockMs: SIGNALSMITH_LIVE_BLOCK_MS,
      splitComputation: true
    }).catch(() => null).then(() => {
      const left = new Float32Array(buffer.getChannelData(0));
      const right = buffer.numberOfChannels > 1
        ? new Float32Array(buffer.getChannelData(1))
        : new Float32Array(buffer.getChannelData(0));
      return stretch.addBuffers([left, right]).then(() => stretch);
    });
  });
}

function copyBufferRegion(ctx, buffer, start, end) {
  const sampleRate = buffer.sampleRate;
  const startFrame = Math.max(0, Math.floor(start * sampleRate));
  const endFrame = Math.min(buffer.length, Math.max(startFrame + 1, Math.ceil(end * sampleRate)));
  const out = ctx.createBuffer(2, endFrame - startFrame, sampleRate);
  for (let channel = 0; channel < 2; channel += 1) {
    const source = buffer.getChannelData(Math.min(channel, buffer.numberOfChannels - 1));
    out.getChannelData(channel).set(source.slice(startFrame, endFrame));
  }
  return out;
}

function connectTrack(name, source, gain, pan) {
  if (!source || !gain || !state.masterGain) return false;
  state.sources[name] = source;
  state.gains[name] = gain;
  if (pan) state.pans[name] = pan;
  try {
    source.connect(gain);
    if (pan) {
      gain.connect(pan);
      pan.connect(state.masterGain);
    } else {
      gain.connect(state.masterGain);
    }
    return true;
  } catch (error) {
    cleanupPlaybackEntries([{ name, source, gain, pan }]);
    console.warn(`Failed to connect ${name} playback graph`, error);
    return false;
  }
}

async function pausePlayback() {
  playbackRequestId += 1;
  state.pausedAt = getPosition();
  state.playing = false;
  updateTransport();
  stopAnimationLoop();
  updateWaveformPlayhead();
  updateTimeLabels();
  updateMixerMeters(true);
  await afterNextPaint();
  if (state.masterGain && state.playContext) {
    const ctx = state.playContext;
    state.masterGain.gain.cancelScheduledValues(ctx.currentTime);
    state.masterGain.gain.setValueAtTime(Math.max(0.0001, state.masterGain.gain.value), ctx.currentTime);
    state.masterGain.gain.exponentialRampToValueAtTime(0.0001, ctx.currentTime + 0.03);
  }
  closePlaybackTimer = window.setTimeout(() => {
    closePlaybackTimer = 0;
    stopPlaybackGraph();
  }, 35);
  suspendPlaybackTimer = window.setTimeout(() => {
    suspendPlaybackTimer = 0;
    suspendPlaybackContextOnPause();
  }, 70);
  updateTranscriptionPlayback();
}

function afterNextPaint() {
  return new Promise((resolve) => {
    requestAnimationFrame(() => requestAnimationFrame(resolve));
  });
}

function stopPlaybackGraph() {
  if (signalsmithFallbackTimer) {
    window.clearTimeout(signalsmithFallbackTimer);
    signalsmithFallbackTimer = 0;
  }
  Object.values(state.sources).forEach((source) => {
    try { if (source.stop) source.stop(0); } catch (error) {}
    try { source.disconnect(); } catch (error) {}
  });
  state.sources = {};
  state.gains = {};
  state.pans = {};
  if (state.masterGain) {
    try { state.masterGain.disconnect(); } catch (error) {}
  }
  if (state.masterAnalyser) {
    try { state.masterAnalyser.disconnect(); } catch (error) {}
  }
  state.masterGain = null;
  state.masterAnalyser = null;
}

async function closePlaybackContext() {
  stopPlaybackGraph();
  const ctx = state.playContext;
  state.playContext = null;
  if (ctx && ctx.state !== 'closed') {
    try { await ctx.close(); } catch (error) {}
  }
}

function suspendPlaybackContextOnPause() {
  const ctx = state.playContext;
  if (!ctx || typeof ctx.suspend !== 'function') return;
  if (state.playing || ctx.state !== 'running') return;
  ctx.suspend().catch((error) => console.warn('Failed to suspend audio context', error));
}

function restartAt(offset) {
  if (state.playing) startPlayback(offset);
}

function getPosition() {
  if (!state.duration) return 0;
  if (!state.playing) return clampOffset(state.pausedAt || 0);
  const elapsed = Math.max(0, performance.now() / 1000 - state.visualStartedAt) * Math.max(0.05, state.speed);
  let position = state.pausedAt + elapsed;
  if (state.loopOn) position = wrapOffset(position);
  return Math.max(0, Math.min(position, state.duration));
}

function clampOffset(offset) {
  const value = Number(offset) || 0;
  if (!state.loopOn) return Math.max(0, Math.min(value, Math.max(0, state.duration - 0.001)));
  return Math.max(getActiveLoopStart(), Math.min(value, Math.max(getActiveLoopStart(), getActiveLoopEnd() - 0.001)));
}

function wrapOffset(offset) {
  const start = getActiveLoopStart();
  const end = getActiveLoopEnd();
  const length = Math.max(0.05, end - start);
  return start + (((offset - start) % length + length) % length);
}

function getActiveLoopStart() {
  return state.loopState > 0 ? Math.max(0, Math.min(state.loopStart, state.duration)) : 0;
}

function getActiveLoopEnd() {
  return state.loopState === 2 ? Math.max(getActiveLoopStart() + 0.05, Math.min(state.loopEnd || state.duration, state.duration)) : state.duration;
}

function getPremiumLimitedEndTime() {
  return state.hasPremium ? (state.duration || 0) : Math.min(state.duration || FREE_PREMIUM_LIMIT_SECONDS, FREE_PREMIUM_LIMIT_SECONDS);
}

function isPastFreePremiumLimit(time) {
  return !state.hasPremium && Number(time) > getPremiumLimitedEndTime() + 0.001;
}

function showPremiumBlockPrompt() {
  showModal(els.premiumModal);
}

function getTrackPitch(name) {
  return state.ignoreDrumsPitch && name === 'drums' ? 0 : state.pitch;
}

function setSpeed(value) {
  const min = state.hasPremium ? 0.25 : 0.95;
  const max = state.hasPremium ? 2 : 1.05;
  const next = Math.round(Math.max(min, Math.min(max, value)) * 100) / 100;
  if (next !== value && !state.hasPremium) showModal(els.premiumModal);
  state.speed = next;
  if (els.speedLabel) els.speedLabel.textContent = `${Math.round(next * 100)}%`;
  restartAt(getPosition());
}

function setPitch(value) {
  const min = state.hasPremium ? -12 : -1;
  const max = state.hasPremium ? 12 : 1;
  const next = Math.max(min, Math.min(max, Math.round(value * 10) / 10));
  if (next !== value && !state.hasPremium) showModal(els.premiumModal);
  state.pitch = next;
  if (els.pitchLabel) els.pitchLabel.textContent = next > 0 ? `+${next}` : String(next);
  renderTranscription();
  drawWaveform();
  restartAt(getPosition());
}

function seekBy(seconds) {
  seekTo(state.loopOn ? wrapOffset(getPosition() + seconds) : Math.max(0, Math.min(state.duration, getPosition() + seconds)));
}

function seekTo(offset) {
  const target = clampOffset(offset);
  state.pausedAt = target;
  if (state.playing) restartAt(target);
  updateWaveformPlayhead();
  updateTimeLabels();
  updateTranscriptionPlayback();
}

function toggleLoop() {
  if (!state.duration) return;
  const minLength = 0.05;
  const loopMax = getPremiumLimitedEndTime();
  const position = getPosition();
  if (state.loopState === 0) {
    if (isPastFreePremiumLimit(position)) {
      showPremiumBlockPrompt();
      return;
    }
    state.loopStart = snapLoopPoint(position, 0, Math.max(0, loopMax - minLength));
    state.loopEnd = state.duration;
    state.loopState = 1;
    state.loopOn = false;
    updateLoopButtonVisual();
    drawWaveform();
    return;
  }

  if (state.loopState === 1) {
    if (isPastFreePremiumLimit(position) || loopMax < state.loopStart + minLength) {
      showPremiumBlockPrompt();
      return;
    }
    let outPoint = snapLoopPoint(position, state.loopStart + minLength, loopMax);
    if (outPoint <= state.loopStart + minLength) {
      outPoint = snapLoopPoint(Math.min(loopMax, state.loopStart + 0.25), state.loopStart + minLength, loopMax);
    }
    state.loopEnd = Math.max(state.loopStart + minLength, Math.min(outPoint, loopMax));
    state.loopState = 2;
    state.loopOn = true;
    updateLoopButtonVisual();
    drawWaveform();
    startPlayback(state.loopStart);
    return;
  }

  state.loopState = 0;
  state.loopOn = false;
  state.loopStart = 0;
  state.loopEnd = state.duration;
  updateLoopButtonVisual();
  drawWaveform();
  restartAt(position);
}

function snapLoopPoint(time, min, max) {
  return state.snapToGrid ? snapToBeat(time, min, max) : Math.max(min, Math.min(max, time));
}

function updateLoopButtonVisual() {
  const button = els.loopBtn;
  if (!button) return;
  button.classList.toggle('is-recording', state.loopState === 1);
  button.classList.toggle('is-active', state.loopState === 2);
}

function applyMixer() {
  const anySolo = MIXER_ROWS.some((name) => state.settings[name].solo);
  AUDIO_TRACKS.forEach((trackName) => {
    const rowName = trackName === 'clickOnset' ? 'click' : trackName;
    const setting = state.settings[rowName];
    const audible = setting && !setting.muted && (!anySolo || setting.solo);
    if (state.gains[trackName]) state.gains[trackName].gain.value = audible ? setting.volume : 0;
    if (state.pans[trackName]) state.pans[trackName].pan.value = setting.pan;
  });
  updateMixerMeters(true);
}

function renderMixer() {
  if (!els.mixerRows) return;
  els.mixerRows.innerHTML = '';
  const available = MIXER_ROWS.filter((name) => name !== 'click' || state.buffers.click || state.rawBuffers.click);
  els.mixerMeta.textContent = i18n.t('stringTrackCount', { count: available.length });
  available.forEach((name) => {
    const setting = state.settings[name];
    const row = document.createElement('div');
    row.className = `mixer-row ${state.selectedStem === name ? 'is-selected' : ''} ${setting.muted ? 'is-muted' : ''}`;
    row.dataset.stem = name === 'click' ? 'metronome' : name;
    row.innerHTML = `
      <span class="stem-dot" data-action="select"></span>
      <span class="stem-label" data-action="select">${getStemLabel(name)}</span>
      <button class="stem-toggle ${setting.muted ? 'is-muted' : ''}" data-action="mute" type="button">M</button>
      <button class="stem-toggle ${setting.solo ? 'is-solo' : ''}" data-action="solo" type="button">S</button>
      <span class="stem-volume-label">VOL</span>
      <span class="stem-volume-wrap"><span class="stem-volume-meter"></span><input class="stem-volume" type="range" min="0" max="1" step="0.01" value="${setting.volume}"></span>
      <div class="pan-knob" title="${i18n.t('stringDragToPan')}"><span></span><em>${formatPanLabel(setting.pan)}</em></div>
    `;
    row.querySelector('.stem-dot').style.background = STEM_COLORS[name];
    row.querySelector('.stem-dot').style.boxShadow = `0 0 0 2px ${colorWithAlpha(STEM_COLORS[name], 0.14)}`;
    const knob = row.querySelector('.pan-knob');
    knob.style.setProperty('--knob-color', STEM_COLORS[name]);
    knob.style.setProperty('--pan-rotation', `${setting.pan * 135}deg`);
    const selectStem = () => {
      state.selectedStem = name === 'click' ? 'drums' : name;
      renderMixer();
      renderTranscription();
    };
    row.addEventListener('click', (event) => {
      if (event.target.closest('button') || event.target.closest('.stem-volume-wrap') || event.target.closest('.pan-knob')) return;
      selectStem();
    });
    row.querySelector('[data-action="mute"]').addEventListener('click', () => {
      setting.muted = !setting.muted;
      renderMixer();
      applyMixer();
    });
    row.querySelector('[data-action="solo"]').addEventListener('click', () => {
      setting.solo = !setting.solo;
      renderMixer();
      applyMixer();
    });
    row.querySelector('.stem-volume').addEventListener('input', (event) => {
      setting.volume = Number(event.target.value);
      applyMixer();
    });
    knob.addEventListener('pointerdown', (event) => startPanDrag(event, name, knob));
    els.mixerRows.appendChild(row);
  });
  updateMixerMeters(true);
}

function formatPanLabel(pan) {
  const value = Math.round((Number(pan) || 0) * 50);
  if (value === 0) return 'C';
  return value < 0 ? `L${Math.abs(value)}` : `R${value}`;
}

function colorWithAlpha(color, alpha) {
  const match = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(String(color || ''));
  if (!match) return `rgba(230,220,135,${alpha})`;
  return `rgba(${parseInt(match[1], 16)},${parseInt(match[2], 16)},${parseInt(match[3], 16)},${alpha})`;
}

function getMixerEffectiveVolume(name) {
  const anySolo = MIXER_ROWS.some((stemName) => state.settings[stemName]?.solo);
  const setting = state.settings[name] || { volume: 1, muted: false, solo: false };
  let volume = Math.max(0, Math.min(1, Number(setting.volume) || 0));
  if (setting.muted) volume = 0;
  if (anySolo && !setting.solo) volume = 0;
  return volume;
}

function estimateStemLevel(name, position) {
  const buffer = state.buffers[name];
  if (!buffer || !buffer.duration || !buffer.length) return 0;
  const sampleRate = buffer.sampleRate || 44100;
  const local = ((Number(position) || 0) % buffer.duration + buffer.duration) % buffer.duration;
  const start = Math.max(0, Math.min(buffer.length - 1, Math.floor(local * sampleRate)));
  const end = Math.min(buffer.length, start + 1024);
  const left = buffer.getChannelData(0);
  const right = buffer.numberOfChannels > 1 ? buffer.getChannelData(1) : left;
  let peak = 0;
  for (let index = start; index < end; index += 32) {
    const value = Math.max(Math.abs(left[index] || 0), Math.abs(right[index] || 0));
    if (value > peak) peak = value;
  }
  return Math.min(1, peak * 3);
}

function updateMixerMeters(force = false) {
  if (!els.mixerRows) return;
  const now = performance.now();
  if (!force && state.playing && now - lastMixerMeterFrameAt < 60) return;
  lastMixerMeterFrameAt = now;
  const position = getPosition();
  MIXER_ROWS.forEach((name) => {
    const row = els.mixerRows.querySelector(`[data-stem="${name === 'click' ? 'metronome' : name}"]`);
    if (!row) return;
    const volume = getMixerEffectiveVolume(name);
    const audible = state.playing && volume > 0.001;
    const meter = row.querySelector('.stem-volume-meter');
    row.classList.toggle('is-audible', audible);
    row.style.opacity = volume > 0.001 ? '' : '0.55';
    if (!meter) return;
    const level = audible ? estimateStemLevel(name, position) * volume : 0;
    meter.style.width = `${Math.round(level * 100)}%`;
    meter.style.background = colorWithAlpha(STEM_COLORS[name], audible ? 0.44 : 0.14);
    meter.style.opacity = audible ? '1' : '0.6';
  });
}

function startPanDrag(event, name, knob) {
  event.preventDefault();
  const startY = event.clientY;
  const startPan = state.settings[name].pan;
  const move = (moveEvent) => {
    const delta = (startY - moveEvent.clientY) / 80;
    const next = Math.max(-1, Math.min(1, startPan + delta));
    state.settings[name].pan = Math.abs(next) < 0.05 ? 0 : next;
    knob.style.setProperty('--pan-rotation', `${state.settings[name].pan * 135}deg`);
    const label = knob.querySelector('em');
    if (label) label.textContent = formatPanLabel(state.settings[name].pan);
    applyMixer();
  };
  const up = () => {
    document.removeEventListener('pointermove', move);
    document.removeEventListener('pointerup', up);
    renderMixer();
  };
  document.addEventListener('pointermove', move);
  document.addEventListener('pointerup', up);
}

function drawWaveform(updateRuler = true) {
  const canvas = els.waveformCanvas;
  if (!canvas) return;
  updateWaveformTimelineWidth();
  const scroller = els.waveformScroller;
  const timeline = els.waveformTimeline;
  const timelineWidth = Math.max(1, Math.round(timeline?.clientWidth || scroller?.scrollWidth || canvas.clientWidth || 640));
  const viewportWidth = Math.max(1, Math.round(scroller?.clientWidth || canvas.clientWidth || 640));
  const scrollLeft = Math.max(0, Math.min(Math.max(0, timelineWidth - viewportWidth), scroller?.scrollLeft || 0));
  canvas.style.width = `${viewportWidth}px`;
  canvas.style.transform = `translate3d(${scrollLeft}px, 0, 0)`;
  const rect = canvas.getBoundingClientRect();
  const rawDpr = Math.max(1, Math.min(2, window.devicePixelRatio || 1));
  const dpr = Math.max(0.5, Math.min(rawDpr, WAVEFORM_MAX_BACKING_WIDTH / Math.max(1, rect.width)));
  const width = Math.max(1, Math.min(WAVEFORM_MAX_BACKING_WIDTH, Math.round(rect.width * dpr)));
  const height = Math.max(1, Math.round(rect.height * dpr));
  if (canvas.width !== width) canvas.width = width;
  if (canvas.height !== height) canvas.height = height;
  const ctx = canvas.getContext('2d');
  ctx.clearRect(0, 0, width, height);
  ctx.fillStyle = 'rgba(0,0,0,0.18)';
  ctx.fillRect(0, 0, width, height);

  const duration = state.duration || 0;
  const viewport = { timelineWidth, viewportWidth, scrollLeft };
  const chordRects = getChordLabelRects(ctx, width, dpr, viewport);
  const showChordPremiumNotice = shouldRenderChordPremiumNotice(viewport);
  state.chordHitRects = chordRects || [];
  const chordBand = hasRenderableChords() || showChordPremiumNotice ? Math.round(32 * dpr) : 0;
  const waveTop = chordBand ? chordBand + Math.round(6 * dpr) : Math.round(8 * dpr);
  const waveHeight = height - waveTop - Math.round(8 * dpr);
  const mid = waveTop + waveHeight / 2;
  ctx.strokeStyle = 'rgba(255,255,255,0.08)';
  ctx.lineWidth = Math.max(1, dpr);
  ctx.beginPath();
  ctx.moveTo(0, mid);
  ctx.lineTo(width, mid);
  ctx.stroke();
  renderBeatGrid(ctx, width, height, waveTop, dpr, viewport);
  renderChords(ctx, chordBand, dpr, chordRects);
  renderChordPremiumNotice(ctx, chordBand, width, dpr, viewport, showChordPremiumNotice);
  const peaks = state.waveformPeaks;
  if (peaks.length && duration && timelineWidth) {
    const maxPeak = peaks.reduce((max, peak) => Math.max(max, Number(peak) || 0), 0);
    const peakScale = maxPeak > 0.001 ? 1 / maxPeak : 1;
    const halfHeight = (waveHeight / 2) * 0.66;
    ctx.strokeStyle = '#E6DC87';
    ctx.lineWidth = Math.max(1, Math.round(1.5 * dpr));
    ctx.beginPath();
    for (let x = 0; x < width; x += 1) {
      const timelineX = scrollLeft + (x / dpr);
      const peakPos = (timelineX / Math.max(1, timelineWidth)) * Math.max(0, peaks.length - 1);
      const peakIndex = Math.floor(peakPos);
      const peakMix = peakPos - peakIndex;
      const peakA = Number(peaks[peakIndex]) || 0;
      const peakB = Number(peaks[Math.min(peaks.length - 1, peakIndex + 1)]) || peakA;
      const peak = Math.max(0, Math.min(1, (peakA + (peakB - peakA) * peakMix) * peakScale));
      const half = Math.max(1, peak * halfHeight);
      ctx.moveTo(x, mid - half);
      ctx.lineTo(x, mid + half);
    }
    ctx.stroke();
  }

  if (state.loopState > 0 && duration) {
    const x1 = timelineTimeToCanvasX(getActiveLoopStart(), viewport, dpr);
    const x2 = timelineTimeToCanvasX(getActiveLoopEnd(), viewport, dpr);
    if (state.loopState === 2) {
      ctx.fillStyle = 'rgba(230,220,135,0.13)';
      ctx.fillRect(x1, waveTop, Math.max(2, x2 - x1), waveHeight);
    }
    ctx.strokeStyle = 'rgba(6,10,18,0.78)';
    ctx.lineWidth = Math.max(2, Math.round(3 * dpr));
    ctx.beginPath();
    ctx.moveTo(x1, waveTop);
    ctx.lineTo(x1, height);
    if (state.loopState === 2) {
      ctx.moveTo(x2, waveTop);
      ctx.lineTo(x2, height);
    }
    ctx.stroke();
    ctx.strokeStyle = '#E6DC87';
    ctx.lineWidth = Math.max(1, Math.round(1.25 * dpr));
    ctx.beginPath();
    ctx.moveTo(x1, waveTop);
    ctx.lineTo(x1, height);
    if (state.loopState === 2) {
      ctx.moveTo(x2, waveTop);
      ctx.lineTo(x2, height);
    }
    ctx.stroke();
  }

  updateWaveformPlayhead();
  updateTimeLabels();
}

function scheduleWaveformZoomUpdate() {
  updateWaveformTimelineWidth();
  updateWaveformPlayhead();
  updateTimeLabels();
  if (waveformZoomTimer) window.clearTimeout(waveformZoomTimer);
  waveformZoomTimer = window.setTimeout(() => {
    waveformZoomTimer = 0;
    drawWaveform();
    syncWaveformScrollToPlayback(true);
  }, 90);
}

function onWaveformScroll() {
  if (waveformScrollRafId) return;
  waveformScrollRafId = window.requestAnimationFrame(() => {
    waveformScrollRafId = 0;
    drawWaveform(false);
  });
}

function renderBeatGrid(ctx, width, height, top, dpr, viewport) {
  if (!state.beats.length || !state.duration) return;
  state.beats.forEach((beat) => {
    const x = timelineTimeToCanvasX(beat.time, viewport, dpr);
    if (x < -2 * dpr || x > width + 2 * dpr) return;
    ctx.strokeStyle = beat.isDownbeat ? 'rgba(255,255,255,0.18)' : 'rgba(255,255,255,0.08)';
    ctx.lineWidth = beat.isDownbeat ? Math.max(1, dpr) : Math.max(0.5, dpr * 0.7);
    ctx.beginPath();
    ctx.moveTo(x, top);
    ctx.lineTo(x, height);
    ctx.stroke();
  });
}

function updateWaveformTimelineWidth() {
  if (!els.waveformTimeline || !els.waveformCanvas) return 0;
  const viewportWidth = Math.max(1, Math.round(els.waveformScroller?.clientWidth || els.waveformWrap?.clientWidth || 640));
  if (!state.duration) {
    els.waveformTimeline.style.width = `${viewportWidth}px`;
    updateWaveformZoomControl(false);
    return viewportWidth;
  }
  const desiredWidth = Math.ceil(state.duration * WAVEFORM_MIN_PX_PER_SECOND);
  const hasOverflow = desiredWidth > viewportWidth;
  const timelineWidth = hasOverflow
    ? Math.round(viewportWidth + (desiredWidth - viewportWidth) * state.waveformZoom)
    : viewportWidth;
  els.waveformTimeline.style.width = `${timelineWidth}px`;
  updateWaveformZoomControl(hasOverflow);
  return timelineWidth;
}

function updateWaveformZoomControl(enabled) {
  const slider = els.waveformZoomSlider;
  if (!slider) return;
  slider.disabled = !enabled;
  slider.value = String(Math.round(state.waveformZoom * 100));
  slider.closest('.waveform-zoom')?.classList.toggle('is-disabled', !enabled);
}

function renderChords(ctx, chordBand, dpr, chordRects) {
  if (!chordBand || !chordRects?.length) return;
  ctx.save();
  ctx.font = `${Math.round(11 * dpr)}px Lato, sans-serif`;
  ctx.textAlign = 'left';
  ctx.textBaseline = 'middle';
  chordRects.forEach((rect) => {
    const { chord } = rect;
    const anchorX = rect.anchorX;
    const pillX = rect.x;
    const pillY = Math.max(5 * dpr, Math.min(chordBand - rect.height - 5 * dpr, 5 * dpr));
    rect.y = pillY;

    ctx.strokeStyle = 'rgba(230,220,135,0.24)';
    ctx.lineWidth = Math.max(1, dpr);
    ctx.beginPath();
    ctx.moveTo(anchorX, pillY + rect.height);
    ctx.lineTo(anchorX, chordBand + Math.round(3 * dpr));
    ctx.stroke();

    ctx.strokeStyle = 'rgba(230,220,135,0.34)';
    ctx.lineWidth = Math.max(1, dpr);
    ctx.fillStyle = 'rgba(230,220,135,0.16)';
    roundRect(ctx, pillX, pillY, rect.width, rect.height, 5 * dpr);
    ctx.fill();
    ctx.stroke();
    ctx.fillStyle = '#ffffff';
    ctx.fillText(chord, pillX + 8 * dpr, pillY + rect.height / 2);
  });
  ctx.restore();
}

function renderChordPremiumNotice(ctx, chordBand, width, dpr, viewport, shouldRender) {
  if (!shouldRender || !chordBand) return;
  const text = getChordPremiumNoticeText();
  const limitX = timelineTimeToCanvasX(getPremiumLimitedEndTime(), viewport, dpr);
  ctx.save();
  ctx.font = `${Math.round(11 * dpr)}px Lato, sans-serif`;
  ctx.textAlign = 'left';
  ctx.textBaseline = 'middle';
  const maxWidth = Math.max(70 * dpr, width - 16 * dpr);
  let label = text;
  if (ctx.measureText(label).width > maxWidth) {
    label = i18n?.t?.('stringGetPremium') || 'Get Premium';
  }
  const labelWidth = Math.min(maxWidth, Math.max(72 * dpr, ctx.measureText(label).width + 16 * dpr));
  const pillHeight = 18 * dpr;
  const pillY = Math.max(5 * dpr, Math.min(chordBand - pillHeight - 5 * dpr, 5 * dpr));
  const preferredX = Math.max(limitX + 8 * dpr, width * 0.5 - labelWidth * 0.5);
  const pillX = Math.max(8 * dpr, Math.min(width - labelWidth - 8 * dpr, preferredX));
  ctx.strokeStyle = 'rgba(230,220,135,0.34)';
  ctx.lineWidth = Math.max(1, dpr);
  ctx.fillStyle = 'rgba(230,220,135,0.14)';
  roundRect(ctx, pillX, pillY, labelWidth, pillHeight, 5 * dpr);
  ctx.fill();
  ctx.stroke();
  ctx.fillStyle = '#E6DC87';
  ctx.fillText(label, pillX + 8 * dpr, pillY + pillHeight / 2);
  ctx.restore();
}

function getChordPremiumNoticeText() {
  return i18n?.t?.('stringPremiumInfoOnTrack') || 'Loops and song analysis are only available in the first minute without premium.';
}

function getChordLabelRects(ctx, width, dpr, viewport) {
  if (!state.chords.length || !state.duration) return [];
  ctx.save();
  ctx.font = `${Math.round(11 * dpr)}px Lato, sans-serif`;
  const usedBeatSlots = {};
  let rects = state.chords.map((segment) => {
    const start = Math.max(0, Math.min(state.duration, Number(segment.start) || 0));
    const end = Math.max(start, Math.min(state.duration, Number(segment.end) || start));
    const chord = transposeChord(segment.chord, state.pitch);
    if (!chord || isNoChordLabel(chord) || end <= start || isChordHiddenByPremium(start)) return null;
    const displayTime = getChordLabelTime(start, usedBeatSlots);
    if (isChordHiddenByPremium(displayTime)) return null;
    const anchorX = timelineTimeToCanvasX(displayTime, viewport, dpr);
    const labelWidth = Math.max(34 * dpr, ctx.measureText(chord).width + 16 * dpr);
    const x = anchorX + 3 * dpr;
    if (x + labelWidth < -8 * dpr || x > width + 8 * dpr) return null;
    return {
      chord,
      anchorX,
      x,
      width: labelWidth,
      height: 18 * dpr,
      y: 0
    };
  }).filter(Boolean);
  ctx.restore();

  if (state.waveformZoom < 0.999) {
    rects.sort((a, b) => a.x - b.x);
    const minGap = 5 * dpr;
    const visible = [];
    rects.forEach((rect) => {
      const previous = visible[visible.length - 1];
      if (previous && rect.x < previous.x + previous.width + minGap) return;
      visible.push(rect);
    });
    rects = visible;
  }
  return rects;
}

function hasRenderableChords() {
  return state.chords.some((segment) => {
    const start = Math.max(0, Math.min(state.duration || Infinity, Number(segment.start) || 0));
    if (isChordHiddenByPremium(start)) return false;
    const chord = transposeChord(segment.chord, state.pitch);
    return chord && !isNoChordLabel(chord);
  });
}

function isChordHiddenByPremium(time) {
  return !state.hasPremium && Number(time) >= getPremiumLimitedEndTime();
}

function shouldRenderChordPremiumNotice(viewport) {
  if (state.hasPremium || !state.duration || state.duration <= FREE_PREMIUM_LIMIT_SECONDS) return false;
  const limit = getPremiumLimitedEndTime();
  const viewportEnd = ((viewport?.scrollLeft || 0) + (viewport?.viewportWidth || 0)) / Math.max(1, viewport?.timelineWidth || 1) * state.duration;
  if (viewportEnd < limit) return false;
  return state.chords.some((segment) => {
    const start = Math.max(0, Math.min(state.duration, Number(segment.start) || 0));
    const chord = transposeChord(segment.chord, state.pitch);
    return start >= limit && chord && !isNoChordLabel(chord);
  });
}

function getChordLabelTime(start, usedBeatSlots) {
  if (!state.beats.length || !state.duration) return start;
  const beats = state.beats.map((beat, index) => {
    const time = Number(beat.time);
    if (!isFinite(time) || time < 0 || time > state.duration) return null;
    return {
      index,
      time,
      isDownbeat: !!beat.isDownbeat,
      distance: Math.abs(time - start)
    };
  }).filter(Boolean);
  const downbeat = beats.filter((beat) => beat.isDownbeat).sort((a, b) => a.distance - b.distance)[0];
  let chosen = downbeat && !usedBeatSlots[downbeat.index] ? downbeat : null;
  if (!chosen) {
    chosen = beats.filter((beat) => !usedBeatSlots[beat.index]).sort((a, b) => a.distance - b.distance)[0];
  }
  if (!chosen) return start;
  usedBeatSlots[chosen.index] = true;
  return chosen.time;
}

function getWaveformChordHit(event) {
  const canvas = els.waveformCanvas;
  if (!canvas || !state.chordHitRects?.length || !event) return null;
  const rect = canvas.getBoundingClientRect();
  if (!rect.width || !rect.height) return null;
  const x = (event.clientX - rect.left) * ((canvas.width || rect.width) / rect.width);
  const y = (event.clientY - rect.top) * ((canvas.height || rect.height) / rect.height);
  for (let i = state.chordHitRects.length - 1; i >= 0; i -= 1) {
    const hit = state.chordHitRects[i];
    if (!hit?.chord) continue;
    if (x >= hit.x && x <= hit.x + hit.width && y >= hit.y && y <= hit.y + hit.height) return hit;
  }
  return null;
}

function isPointerInChordBand(event) {
  const canvas = els.waveformCanvas;
  if (!canvas || !state.chordHitRects?.length || !event) return false;
  const rect = canvas.getBoundingClientRect();
  if (!rect.width || !rect.height) return false;
  const y = (event.clientY - rect.top) * ((canvas.height || rect.height) / rect.height);
  const bandBottom = state.chordHitRects.reduce((max, hit) => Math.max(max, (hit?.y || 0) + (hit?.height || 0)), 0);
  const dpr = Math.max(1, (canvas.height || rect.height) / rect.height);
  return y <= bandBottom + Math.round(8 * dpr);
}

function showGuitarChordPanel(chordName) {
  if (!els.guitarChordPanel || !els.guitarChordTitle || !els.guitarChordGrid || !els.guitarChordEmpty) return;
  const parsed = parseGuitarChordName(chordName);
  let voicings = parsed ? getGuitarChordVoicings(parsed) : [];
  const displayName = parsed ? parsed.display : String(chordName || '').trim();
  if (els.guitarChordPanel.classList.contains('vfcGuitarChordPanelVisible') && displayName && displayName === state.openChordVoicingName) {
    hideGuitarChordPanel();
    return;
  }

  els.guitarChordTitle.textContent = '';
  const titleLabel = document.createElement('span');
  titleLabel.className = 'vfcGuitarChordTitleLabel';
  titleLabel.textContent = displayName ? i18n.t('stringGuitarVoicingsWithColon') : i18n.t('stringGuitarVoicings');
  els.guitarChordTitle.appendChild(titleLabel);
  if (displayName) {
    const titleChord = document.createElement('span');
    titleChord.className = 'vfcGuitarChordTitleChord';
    titleChord.textContent = displayName;
    els.guitarChordTitle.appendChild(titleChord);
  }

  els.guitarChordGrid.textContent = '';
  els.guitarChordEmpty.textContent = '';
  if (!voicings.length && parsed) {
    const simpler = simplifyGuitarChordLabel(parsed.display);
    if (simpler && simpler !== parsed.display) {
      const fallback = parseGuitarChordName(simpler);
      voicings = fallback ? getGuitarChordVoicings(fallback) : [];
    }
  }

  if (!voicings.length) {
    els.guitarChordEmpty.textContent = i18n.t('stringNoGuitarVoicings');
  } else {
    voicings.forEach((voicing) => {
      const card = document.createElement('div');
      card.className = 'vfcGuitarChordCard';
      const diagram = document.createElement('div');
      diagram.className = 'vfcGuitarChordDiagram';
      diagram.innerHTML = renderGuitarChordSvg(voicing);
      card.appendChild(diagram);
      els.guitarChordGrid.appendChild(card);
    });
  }

  els.guitarChordPanel.hidden = false;
  els.guitarChordPanel.classList.add('vfcGuitarChordPanelVisible');
  state.openChordVoicingName = displayName || '';
}

function hideGuitarChordPanel() {
  if (els.guitarChordPanel) {
    els.guitarChordPanel.classList.remove('vfcGuitarChordPanelVisible');
    els.guitarChordPanel.hidden = true;
  }
  state.openChordVoicingName = '';
}

function timeToX(time, width) {
  return state.duration ? Math.max(0, Math.min(width, (time / state.duration) * width)) : 0;
}

function timelineTimeToCanvasX(time, viewport, dpr) {
  if (!state.duration || !viewport?.timelineWidth) return 0;
  return (((time / state.duration) * viewport.timelineWidth) - viewport.scrollLeft) * dpr;
}

function getWaveformTimelineWidth() {
  return Math.max(
    1,
    Math.round(els.waveformTimeline?.clientWidth || els.waveformScroller?.scrollWidth || els.waveformCanvas?.clientWidth || 1)
  );
}

function getWaveformTimelineX(clientX) {
  const scroller = els.waveformScroller;
  const rect = scroller?.getBoundingClientRect() || els.waveformCanvas.getBoundingClientRect();
  const timelineWidth = getWaveformTimelineWidth();
  return Math.max(0, Math.min(timelineWidth, (clientX - rect.left) + (scroller?.scrollLeft || 0)));
}

function getWaveformHitTarget(event) {
  if (!state.duration || !event) return 'seek';
  const timelineWidth = getWaveformTimelineWidth();
  const pointerX = getWaveformTimelineX(event.clientX);
  const markerRadius = 11;
  const playheadRadius = 9;
  const candidates = [];

  const addCandidate = (type, x, radius, priority = 0) => {
    const distance = Math.abs(pointerX - x);
    if (distance <= radius) candidates.push({ type, x, distance, priority });
  };

  if (state.loopState > 0) {
    addCandidate('loopStart', timeToX(getActiveLoopStart(), timelineWidth), markerRadius, 1);
    if (state.loopState === 2) {
      addCandidate('loopEnd', timeToX(getActiveLoopEnd(), timelineWidth), markerRadius, 1);
    }
  }
  addCandidate('playhead', timeToX(getPosition(), timelineWidth), playheadRadius, 0);

  if (!candidates.length) return 'seek';

  const start = candidates.find((candidate) => candidate.type === 'loopStart');
  const end = candidates.find((candidate) => candidate.type === 'loopEnd');
  if (start && end) {
    const midpoint = (start.x + end.x) / 2;
    const sideTarget = pointerX >= midpoint ? end : start;
    const playhead = candidates.find((candidate) => candidate.type === 'playhead');
    if (playhead && playhead.distance + 2 < sideTarget.distance) return playhead.type;
    return sideTarget.type;
  }

  candidates.sort((a, b) => (a.distance - b.distance) || (a.priority - b.priority));
  return candidates[0].type;
}

function xToTime(clientX) {
  const timelineWidth = getWaveformTimelineWidth();
  const x = getWaveformTimelineX(clientX);
  return timelineWidth && state.duration ? (x / timelineWidth) * state.duration : 0;
}

function getWaveformDisplayPosition() {
  return dragState?.type === 'playhead' ? clampOffset(state.pausedAt || 0) : getPosition();
}

function updateWaveformPlayhead() {
  const playhead = els.waveformPlayhead;
  const canvas = els.waveformCanvas;
  if (!playhead || !canvas || !state.duration) return;
  const width = getWaveformTimelineWidth();
  const x = timeToX(getWaveformDisplayPosition(), width);
  playhead.style.transform = `translate3d(${Math.max(0, x)}px, 0, 0)`;
}

function syncWaveformScrollToPlayback(force = false) {
  const scroller = els.waveformScroller;
  const canvas = els.waveformCanvas;
  if (!scroller || !canvas || dragState) return;
  const maxScroll = scroller.scrollWidth - scroller.clientWidth;
  if (maxScroll <= 0 || !state.duration) return;

  const timelineWidth = getWaveformTimelineWidth();
  const playheadX = (getWaveformDisplayPosition() / state.duration) * timelineWidth;
  const left = scroller.scrollLeft;
  const width = scroller.clientWidth;
  const keepLeft = left + width * 0.28;
  const keepRight = left + width * 0.72;
  if (force || playheadX < keepLeft || playheadX > keepRight) {
    scroller.scrollLeft = Math.max(0, Math.min(maxScroll, playheadX - width * 0.38));
  }
}

function autoScrollWaveformDuringDrag(event) {
  const scroller = els.waveformScroller;
  if (!scroller || !event) return false;
  const maxScroll = scroller.scrollWidth - scroller.clientWidth;
  if (maxScroll <= 0) return false;
  const rect = scroller.getBoundingClientRect();
  const edge = Math.min(56, Math.max(28, rect.width * 0.16));
  let delta = 0;
  if (event.clientX < rect.left + edge) {
    delta = -Math.ceil(((rect.left + edge - event.clientX) / edge) * 18);
  } else if (event.clientX > rect.right - edge) {
    delta = Math.ceil(((event.clientX - (rect.right - edge)) / edge) * 18);
  }
  if (!delta) return false;
  const before = scroller.scrollLeft;
  scroller.scrollLeft = Math.max(0, Math.min(maxScroll, scroller.scrollLeft + delta));
  return before !== scroller.scrollLeft;
}

function onWaveformPointerDown(event) {
  if (!state.duration) return;
  const chordHit = getWaveformChordHit(event);
  if (chordHit) {
    event.preventDefault();
    event.stopPropagation();
    showGuitarChordPanel(chordHit.chord);
    return;
  }
  event.preventDefault();
  const time = xToTime(event.clientX);
  const current = getPosition();
  const type = getWaveformHitTarget(event);
  if (type === 'seek' && state.playing) {
    const target = clampOffset(time);
    state.pausedAt = target;
    updateWaveformPlayhead();
    updateTimeLabels();
    updateTranscriptionPlayback();
    startPlayback(target);
    return;
  }
  dragState = { type, wasPlaying: state.playing };
  if (type === 'playhead' && state.playing) {
    playbackRequestId += 1;
    state.pausedAt = current;
    state.playing = false;
    stopAnimationLoop();
    updateTransport();
    stopPlaybackGraph();
  }
  if (type === 'seek') {
    if (state.playing) {
      state.pausedAt = clampOffset(time);
      updateWaveformPlayhead();
      updateTimeLabels();
      updateTranscriptionPlayback();
    } else {
      seekTo(time);
    }
    dragState.type = 'playhead';
  }
  document.addEventListener('pointermove', onWaveformPointerDrag);
  document.addEventListener('pointerup', onWaveformPointerUp);
  document.addEventListener('pointercancel', onWaveformPointerUp);
}

function onWaveformPointerMove(event) {
  if (!state.duration || dragState) return;
  if (getWaveformChordHit(event)) {
    els.waveformCanvas.style.cursor = 'pointer';
    return;
  }
  if (isPointerInChordBand(event)) {
    els.waveformCanvas.style.cursor = 'default';
    return;
  }
  const target = getWaveformHitTarget(event);
  if (target === 'loopStart' || target === 'loopEnd') {
    els.waveformCanvas.style.cursor = 'ew-resize';
  } else if (target === 'playhead') {
    els.waveformCanvas.style.cursor = 'grab';
  } else {
    els.waveformCanvas.style.cursor = 'crosshair';
  }
}

function onWaveformPointerDrag(event) {
  if (!dragState) return;
  event.preventDefault();
  autoScrollWaveformDuringDrag(event);
  let time = xToTime(event.clientX);
  const minLength = 0.05;
  if (dragState.type === 'loopStart') {
    if (isPastFreePremiumLimit(time)) {
      if (!dragState.premiumPromptShown) {
        showPremiumBlockPrompt();
        dragState.premiumPromptShown = true;
      }
      time = getPremiumLimitedEndTime();
    }
    const maxStart = Math.min(getActiveLoopEnd() - minLength, getPremiumLimitedEndTime() - minLength);
    state.loopStart = snapLoopPoint(time, 0, Math.max(0, maxStart));
    if (state.loopState === 0) state.loopState = 1;
    state.loopOn = state.loopState === 2;
  } else if (dragState.type === 'loopEnd') {
    if (isPastFreePremiumLimit(time)) {
      if (!dragState.premiumPromptShown) {
        showPremiumBlockPrompt();
        dragState.premiumPromptShown = true;
      }
      time = getPremiumLimitedEndTime();
    }
    const minEnd = getActiveLoopStart() + minLength;
    const maxEnd = getPremiumLimitedEndTime();
    if (minEnd > maxEnd) {
      if (!dragState.premiumPromptShown) {
        showPremiumBlockPrompt();
        dragState.premiumPromptShown = true;
      }
      return;
    }
    state.loopEnd = snapLoopPoint(time, minEnd, maxEnd);
    state.loopState = 2;
    state.loopOn = true;
  } else {
    state.pausedAt = clampOffset(time);
  }
  updateLoopButtonVisual();
  scheduleWaveformDragUpdate();
}

function scheduleWaveformDragUpdate() {
  if (waveformDragRafId) return;
  waveformDragRafId = requestAnimationFrame(() => {
    waveformDragRafId = 0;
    if (dragState?.type === 'playhead') {
      updateWaveformPlayhead();
      updateTimeLabels();
      updateTranscriptionPlayback();
    } else {
      drawWaveform();
      updateTranscriptionPlayback();
    }
  });
}

function onWaveformPointerUp() {
  if (waveformDragRafId) {
    cancelAnimationFrame(waveformDragRafId);
    waveformDragRafId = 0;
  }
  const shouldRestart = dragState && state.playing;
  const wasPlaying = dragState?.wasPlaying;
  const offset = dragState?.type === 'playhead' ? state.pausedAt : clampOffset(getPosition());
  const endedType = dragState?.type;
  dragState = null;
  document.removeEventListener('pointermove', onWaveformPointerDrag);
  document.removeEventListener('pointerup', onWaveformPointerUp);
  document.removeEventListener('pointercancel', onWaveformPointerUp);
  if (endedType === 'playhead') {
    updateWaveformPlayhead();
    updateTimeLabels();
  } else {
    drawWaveform();
  }
  updateTranscriptionPlayback();
  if (wasPlaying || shouldRestart) startPlayback(offset);
}

function snapToBeat(time, min = 0, max = state.duration || Infinity) {
  const minValue = Math.max(0, Number.isFinite(Number(min)) ? Number(min) : 0);
  let maxValue = Number.isFinite(Number(max)) ? Number(max) : (state.duration || Infinity);
  if (state.duration) maxValue = Math.min(state.duration, maxValue);
  if (maxValue < minValue) maxValue = minValue;
  const value = Math.max(minValue, Math.min(maxValue, Number.isFinite(Number(time)) ? Number(time) : minValue));
  if (!state.beats.length) return value;
  const candidates = state.beats
    .map((beat) => Number(beat.time))
    .filter((beatTime) => isFinite(beatTime) && beatTime >= minValue && beatTime <= maxValue);
  if (!candidates.length) return value;
  return candidates.reduce((best, beatTime) => {
    return Math.abs(beatTime - value) < Math.abs(best - value) ? beatTime : best;
  }, candidates[0]);
}

function startSplitDrag(event) {
  event.preventDefault();
  const row = els.studioSplitRow;
  const rect = row.getBoundingClientRect();
  const move = (moveEvent) => {
    const pct = ((moveEvent.clientX - rect.left) / rect.width) * 100;
    state.mixerPct = Math.max(26, Math.min(70, pct));
    row.style.setProperty('--mixer-pct', `${state.mixerPct}%`);
    renderTranscription();
  };
  const up = () => {
    document.removeEventListener('pointermove', move);
    document.removeEventListener('pointerup', up);
  };
  document.addEventListener('pointermove', move);
  document.addEventListener('pointerup', up);
}

function setTranscriptionView(view) {
  state.view = view;
  document.querySelectorAll('.tab').forEach((tab) => tab.classList.toggle('is-active', tab.dataset.view === view));
  document.querySelectorAll('.transcription-view').forEach((panel) => panel.classList.remove('is-active'));
  document.getElementById(`${view}View`)?.classList.add('is-active');
  renderTranscription();
}

function getTranscriptionEmptyMessage(stem) {
  return stem === 'drums'
    ? i18n.t('stringDrumTranscriptionUnavailable')
    : i18n.t('stringTranscriptionPending');
}

function renderTranscription() {
  const stem = state.selectedStem;
  const rawNotes = stem === 'drums' ? [] : (state.transcriptions[stem] || []);
  const transposedNotes = transposeNotes(rawNotes);
  const transcriptionBlocked = stem !== 'drums' && hasPremiumLimitedTranscription(transposedNotes);
  const notes = getPremiumLimitedTranscriptionNotes(transposedNotes);
  state.transcriptionPremiumBlocked = transcriptionBlocked;
  if (els.transcriptionPanel) els.transcriptionPanel.dataset.stem = stem;
  if (els.transcriptionStemName) els.transcriptionStemName.textContent = getStemLabel(stem);
  if (els.transcriptionStemDot) {
    els.transcriptionStemDot.style.background = STEM_COLORS[stem] || '#E6DC87';
    els.transcriptionStemDot.style.boxShadow = `0 0 0 2px ${colorWithAlpha(STEM_COLORS[stem] || '#E6DC87', 0.14)}`;
  }
  if (els.transcriptionMeta) els.transcriptionMeta.textContent = notes.length ? `${notes.length} notes` : getTranscriptionEmptyMessage(stem);
  renderPiano(notes, stem);
  renderFretboard(notes, stem);
  renderSheet(notes, stem);
  renderTranscriptionPremiumOverlays(transcriptionBlocked);
  updateTranscriptionPlayback();
}

function transposeNotes(notes) {
  const pitch = Math.round(Number(state.pitch) || 0);
  if (!pitch) return notes || [];
  return (notes || []).map((note) => ({
    ...note,
    midiPitch: Math.max(0, Math.min(127, Math.round(note.midiPitch) + pitch))
  }));
}

function getPremiumLimitedTranscriptionNotes(notes) {
  if (state.hasPremium) return notes || [];
  const limit = getPremiumLimitedEndTime();
  return (notes || []).map((note) => {
    const start = Number(note.startTime) || 0;
    const end = Math.max(start + 0.04, Number(note.endTime) || start + 0.08);
    if (start >= limit) return null;
    return end > limit ? { ...note, endTime: limit } : note;
  }).filter(Boolean);
}

function hasPremiumLimitedTranscription(notes) {
  if (state.hasPremium || !state.duration || state.duration <= FREE_PREMIUM_LIMIT_SECONDS) return false;
  const limit = getPremiumLimitedEndTime();
  if (!(notes || []).length) return true;
  return (notes || []).some((note) => {
    const start = Number(note.startTime) || 0;
    const end = Math.max(start, Number(note.endTime) || start);
    return start >= limit || end > limit;
  }) || state.duration > limit;
}

function renderTranscriptionPremiumOverlays(enabled) {
  ['sheetView', 'pianoView', 'fretboardView'].forEach((id) => {
    const view = els[id];
    if (!view) return;
    view.querySelector('.transcription-premium-overlay')?.remove();
    if (!enabled) return;
    const overlay = document.createElement('div');
    const card = document.createElement('div');
    const title = document.createElement('strong');
    const copy = document.createElement('span');
    overlay.className = 'transcription-premium-overlay';
    card.className = 'transcription-premium-card';
    overlay.hidden = true;
    title.textContent = i18n?.t?.('stringGetPremium') || 'Get Premium';
    copy.textContent = getChordPremiumNoticeText();
    card.append(title, copy);
    overlay.appendChild(card);
    view.appendChild(overlay);
  });
}

function updateTranscriptionPremiumOverlayVisibility() {
  const shouldShow = !!state.transcriptionPremiumBlocked && getWaveformDisplayPosition() >= getPremiumLimitedEndTime() - 0.001;
  document.querySelectorAll('.transcription-premium-overlay').forEach((overlay) => {
    overlay.hidden = !shouldShow;
  });
}

function renderPiano(notes, stem) {
  const view = els.pianoView;
  if (!view) return;
  view.innerHTML = '';
  const roll = document.createElement('div');
  const content = document.createElement('div');
  const lanes = document.createElement('div');
  const notesLayer = document.createElement('div');
  const hitLine = document.createElement('div');
  const keysEl = document.createElement('div');
  const empty = document.createElement('div');

  roll.className = 'vfcPianoRollPreview vfcTranscriptionPanelActive';
  content.className = 'vfcPianoRollContent';
  lanes.className = 'vfcPianoRollLanes';
  notesLayer.className = 'vfcPianoRollNotes';
  hitLine.className = 'vfcPianoRollHitLine';
  keysEl.className = 'vfcPianoKeys';
  empty.className = 'vfcPianoRollEmpty';
  empty.textContent = notes.length ? '' : getTranscriptionEmptyMessage(stem);

  content.appendChild(lanes);
  content.appendChild(notesLayer);
  content.appendChild(hitLine);
  content.appendChild(keysEl);
  roll.appendChild(content);
  roll.appendChild(empty);
  view.appendChild(roll);
  installHorizontalOverscrollGuard(roll);

  state.piano = null;
  empty.style.display = notes.length ? 'none' : 'flex';
  if (!notes.length) return;

  const piano = buildExtensionPianoLayout(notes);
  content.style.width = `${piano.contentWidth}px`;
  content.style.minWidth = `${piano.contentWidth}px`;
  const color = '#E6DC87';
  const noteEls = [];

  piano.whites.forEach((key) => {
    const el = document.createElement('span');
    el.className = 'vfcPianoKey vfcPianoKeyWhite';
    el.dataset.keyId = key.id;
    el.dataset.midi = key.midi;
    el.style.left = `${key.left}%`;
    el.style.width = `${key.width}%`;
    keysEl.appendChild(el);
    key.element = el;
  });

  piano.blacks.forEach((key) => {
    const el = document.createElement('span');
    el.className = 'vfcPianoKey vfcPianoKeyBlack';
    el.dataset.keyId = key.id;
    el.dataset.midi = key.midi;
    el.style.left = `${key.left}%`;
    el.style.width = `${key.width}%`;
    keysEl.appendChild(el);
    key.element = el;
  });

  piano.lanes.forEach((left) => {
    const el = document.createElement('span');
    el.className = 'vfcPianoRollLane';
    el.style.left = `${left}%`;
    lanes.appendChild(el);
  });

  const visibleNotes = notes;
  const amplitudes = visibleNotes.map((note) => Math.max(0, Math.min(1, Number(note.amplitude) || 0)));
  const minAmplitude = amplitudes.length ? Math.min(...amplitudes) : 0;
  const maxAmplitude = amplitudes.length ? Math.max(...amplitudes) : 1;
  const amplitudeSpan = Math.max(0.0001, maxAmplitude - minAmplitude);

  visibleNotes.forEach((note) => {
    const key = getExtensionPianoKeyForPitch(note.midiPitch, piano);
    if (!key) return;
    const el = document.createElement('span');
    el.className = 'vfcRollNote';
    el.title = noteName(note.midiPitch);
    el.dataset.keyId = key.id;
    el.style.left = `${key.center - key.noteWidth / 2}%`;
    el.style.width = `${key.noteWidth}%`;
    el.style.background = color;
    el.style.color = color;
    el.style.display = 'none';
    notesLayer.appendChild(el);
    noteEls.push({
      note,
      element: el,
      key,
      velocityAlpha: mapExtensionVelocityAlpha(note.amplitude, minAmplitude, amplitudeSpan)
    });
  });

  state.piano = {
    notes: noteEls,
    keys: piano.orderedKeys,
    notesLayer,
    roll,
    lookahead: 2.6,
    rollHeight: notesLayer.clientHeight || 177
  };

  requestAnimationFrame(() => {
    if (!roll || !roll.scrollWidth || roll.scrollWidth <= roll.clientWidth) return;
    const centerX = (piano.centerPercent / 100) * roll.scrollWidth;
    roll.scrollLeft = Math.max(0, Math.min(roll.scrollWidth - roll.clientWidth, centerX - roll.clientWidth * 0.5));
  });
}

function noteName(midiPitch) {
  const pitch = Math.round(Number(midiPitch) || 0);
  return `${NOTE_NAMES[((pitch % 12) + 12) % 12]}${Math.floor(pitch / 12) - 1}`;
}

function installHorizontalOverscrollGuard(scroller) {
  if (!scroller || scroller.dataset.horizontalOverscrollGuard === '1') return;
  scroller.dataset.horizontalOverscrollGuard = '1';
  let touchStartX = 0;
  let touchStartY = 0;

  const canScroll = () => scroller.scrollWidth > scroller.clientWidth + 1;
  const isAtStart = () => scroller.scrollLeft <= 0;
  const isAtEnd = () => scroller.scrollLeft >= scroller.scrollWidth - scroller.clientWidth - 1;
  const shouldBlock = (deltaX, deltaY) => {
    if (!canScroll() || Math.abs(deltaX) <= Math.abs(deltaY)) return false;
    return (deltaX < 0 && isAtStart()) || (deltaX > 0 && isAtEnd());
  };

  scroller.addEventListener('wheel', (event) => {
    if (shouldBlock(event.deltaX, event.deltaY)) event.preventDefault();
  }, { passive: false });

  scroller.addEventListener('touchstart', (event) => {
    const touch = event.touches?.[0];
    if (!touch) return;
    touchStartX = touch.clientX;
    touchStartY = touch.clientY;
  }, { passive: true });

  scroller.addEventListener('touchmove', (event) => {
    const touch = event.touches?.[0];
    if (!touch) return;
    const deltaX = touchStartX - touch.clientX;
    const deltaY = touchStartY - touch.clientY;
    if (shouldBlock(deltaX, deltaY)) event.preventDefault();
  }, { passive: false });
}

function mapExtensionVelocityAlpha(amplitude, minAmplitude, amplitudeSpan) {
  const value = Math.max(0, Math.min(1, Number(amplitude) || 0));
  const normalized = amplitudeSpan > 0.0001 ? (value - minAmplitude) / amplitudeSpan : value;
  return 0.1 + Math.max(0, Math.min(1, normalized)) * 0.9;
}

function buildExtensionPianoLayout(notes) {
  const numWhite = 49;
  const whiteKeyWidthPx = 21.735;
  const whiteWidth = 100 / numWhite;
  const notePitches = notes
    .map((note) => Math.round(Number(note.midiPitch) || 60))
    .filter((pitch) => pitch >= 21 && pitch <= 108);
  const low = notePitches.length ? Math.min(...notePitches) : 48;
  const high = notePitches.length ? Math.max(...notePitches) : 72;
  const base = findExtensionPianoBaseMidi(low, high, numWhite);
  const whites = [];
  let midi = base;
  while (whites.length < numWhite) {
    if (isExtensionWhiteKey(midi)) {
      const index = whites.length;
      whites.push({
        id: `w${index}`,
        type: 'white',
        midi,
        left: index * whiteWidth,
        width: whiteWidth,
        center: (index + 0.5) * whiteWidth,
        noteWidth: whiteWidth * 0.6
      });
    }
    midi += 1;
  }

  const blacks = [];
  for (let i = 0; i < whites.length - 1; i += 1) {
    if (whites[i + 1].midi - whites[i].midi !== 2) continue;
    const blackWidth = whiteWidth * 0.6;
    const center = (i + 1) * whiteWidth;
    blacks.push({
      id: `b${i}`,
      type: 'black',
      midi: whites[i].midi + 1,
      left: center - blackWidth / 2,
      width: blackWidth,
      center,
      noteWidth: blackWidth * 0.92
    });
  }

  const lanes = [];
  for (let lane = 1; lane < numWhite; lane += 1) lanes.push(lane * whiteWidth);
  const orderedKeys = whites.concat(blacks).sort((a, b) => a.midi - b.midi || a.center - b.center);
  const contentWidth = numWhite * whiteKeyWidthPx;
  const centerPitch = getDensePianoCenterPitch(notePitches, low, high);
  const centerKey = getExtensionPianoKeyForPitch(centerPitch, { orderedKeys });
  const lowKey = getExtensionPianoKeyForPitch(low, { orderedKeys }) || orderedKeys[0];
  const highKey = getExtensionPianoKeyForPitch(high, { orderedKeys }) || orderedKeys[orderedKeys.length - 1];
  const centerPercent = centerKey
    ? centerKey.center
    : lowKey && highKey ? (lowKey.center + highKey.center) / 2 : 50;

  return {
    base,
    whites,
    blacks,
    orderedKeys,
    lanes,
    contentWidth,
    centerPercent
  };
}

function getDensePianoCenterPitch(notePitches, low, high) {
  if (!notePitches.length) return Math.round((low + high) / 2);
  const windowSemitones = 18;
  let bestCenter = notePitches[0];
  let bestScore = -Infinity;

  notePitches.forEach((candidate) => {
    let score = 0;
    notePitches.forEach((pitch) => {
      const distance = Math.abs(pitch - candidate);
      if (distance <= windowSemitones) score += 1 - distance / (windowSemitones + 1);
    });
    if (score > bestScore) {
      bestScore = score;
      bestCenter = candidate;
    }
  });

  const clustered = notePitches.filter((pitch) => Math.abs(pitch - bestCenter) <= windowSemitones);
  const center = clustered.length
    ? clustered.reduce((sum, pitch) => sum + pitch, 0) / clustered.length
    : bestCenter;
  return Math.max(low, Math.min(high, Math.round(center)));
}

function findExtensionPianoBaseMidi(low, high, numWhite) {
  const whiteNotes = [0, 2, 4, 5, 7, 9, 11];
  const center = Math.round((low + high) / 2);
  let bestBase = center - 10;
  while (!isExtensionWhiteKey(bestBase)) bestBase -= 1;
  let bestScore = Infinity;

  for (let candidate = 21; candidate <= 108; candidate += 1) {
    if (!isExtensionWhiteKey(candidate)) continue;
    let whites = 0;
    let top = candidate;
    for (let midi = candidate; midi <= 108 && whites < numWhite; midi += 1) {
      if (whiteNotes.includes(((midi % 12) + 12) % 12)) {
        top = midi;
        whites += 1;
      }
    }
    if (whites < numWhite) continue;
    const under = Math.max(0, candidate - low);
    const over = Math.max(0, high - top);
    const centerPenalty = Math.abs(((candidate + top) / 2) - center) * 0.12;
    const score = under * 10 + over * 10 + centerPenalty;
    if (score < bestScore) {
      bestScore = score;
      bestBase = candidate;
    }
  }
  return bestBase;
}

function isExtensionWhiteKey(midiPitch) {
  const pitchClass = ((Math.round(Number(midiPitch) || 0) % 12) + 12) % 12;
  return pitchClass === 0 || pitchClass === 2 || pitchClass === 4 || pitchClass === 5 || pitchClass === 7 || pitchClass === 9 || pitchClass === 11;
}

function getExtensionPianoKeyForPitch(midiPitch, piano) {
  const pitch = Math.round(Number(midiPitch));
  if (!Number.isFinite(pitch)) return null;
  return piano.orderedKeys.find((key) => key.midi === pitch) || null;
}

function renderFretboard(notes, stem) {
  const view = els.fretboardView;
  if (!view) return;
  view.innerHTML = '';

  const controls = document.createElement('div');
  const tuning = document.createElement('span');
  const positionStepper = createFretboardStepper('position', i18n.t('stringPositionShort'), state.fretboardPosition);
  const spanStepper = createFretboardStepper('span', i18n.t('stringSpan'), state.fretboardSpan);
  const stage = document.createElement('div');
  const fretsEl = document.createElement('div');
  const stringsEl = document.createElement('div');
  const notesEl = document.createElement('div');
  const hit = document.createElement('div');
  const empty = document.createElement('div');

  controls.className = 'fretboard-controls';
  tuning.className = 'fretboard-tuning';
  stage.className = 'fretboard-stage';
  fretsEl.className = 'fretboard-frets';
  stringsEl.className = 'fretboard-strings';
  notesEl.className = 'fretboard-notes';
  hit.className = 'fret-hit-line';
  empty.className = 'fretboard-empty';

  controls.append(tuning, positionStepper, spanStepper);
  stage.append(fretsEl, stringsEl, notesEl, hit);
  view.append(controls, stage, empty);

  const layout = getFretboardLayout(stem);
  const mapped = buildFretboardNotes(notes, layout);
  tuning.textContent = layout.label;
  positionStepper.querySelector('[data-fret-value]').textContent = String(layout.position);
  spanStepper.querySelector('[data-fret-value]').textContent = String(layout.span);

  layout.frets.forEach((fret, index) => {
    const line = document.createElement('span');
    line.className = 'fret-line';
    if (index === 0) line.classList.add('fret-line-nut');
    line.style.left = `${fret.left}%`;
    fretsEl.appendChild(line);

    if (index < layout.span) {
      const label = document.createElement('em');
      label.textContent = String(layout.position + index);
      label.style.left = `${(fret.left + layout.frets[index + 1].left) / 2}%`;
      fretsEl.appendChild(label);
    }
  });

  layout.strings.forEach((stringInfo, index) => {
    const y = getFretboardStringY(index, layout.strings.length);
    const line = document.createElement('span');
    line.className = 'fret-string';
    line.style.top = `${y}%`;
    stringsEl.appendChild(line);

    const label = document.createElement('b');
    label.textContent = stringInfo.name;
    label.style.top = `${y}%`;
    stringsEl.appendChild(label);
  });

  empty.textContent = notes.length ? i18n.t('stringNoNotesInPosition') : getTranscriptionEmptyMessage(stem);
  empty.style.display = mapped.length ? 'none' : 'flex';

  const visibleNotes = mapped;
  const amplitudes = visibleNotes.map((entry) => Math.max(0, Math.min(1, Number(entry.note.amplitude) || 0)));
  const minAmplitude = amplitudes.length ? Math.min(...amplitudes) : 0;
  const maxAmplitude = amplitudes.length ? Math.max(...amplitudes) : 1;
  const amplitudeSpan = Math.max(0.0001, maxAmplitude - minAmplitude);
  const entries = [];

  visibleNotes.forEach((entry, entryIndex) => {
    const group = [entry.primary].concat(entry.alternatives || []);
    group.forEach((candidate, candidateIndex) => {
      const el = document.createElement('span');
      el.className = `fret-note ${candidateIndex > 0 ? 'fret-note-ghost' : 'fret-note-primary'}`;
      el.textContent = String(candidate.fret);
      el.title = `${noteName(entry.note.midiPitch)} string ${candidate.stringName} fret ${candidate.fret}${candidateIndex > 0 ? ' alternative' : ''}`;
      el.dataset.fretEntry = String(entryIndex);
      el.dataset.primary = candidateIndex === 0 ? 'true' : 'false';
      el.style.display = 'none';
      notesEl.appendChild(el);
      entries.push({
        note: entry.note,
        element: el,
        candidate,
        isPrimary: candidateIndex === 0,
        velocityAlpha: mapExtensionVelocityAlpha(entry.note.amplitude, minAmplitude, amplitudeSpan)
      });
    });
  });

  state.fretboard = {
    notes: entries,
    strings: layout.strings,
    layout,
    lookahead: 2.8,
    hitX: 22
  };
}

function createFretboardStepper(kind, label, value) {
  const el = document.createElement('div');
  el.className = 'fretboard-stepper';
  el.innerHTML = `
    <button type="button" data-fret-action="${kind}-down">-</button>
    <span>${label} <strong data-fret-value="${kind}">${value}</strong></span>
    <button type="button" data-fret-action="${kind}-up">+</button>
  `;
  return el;
}

function handleFretboardControl(action) {
  if (action === 'position-down') state.fretboardPosition -= 1;
  if (action === 'position-up') state.fretboardPosition += 1;
  if (action === 'span-down') state.fretboardSpan -= 1;
  if (action === 'span-up') state.fretboardSpan += 1;
  state.fretboardPosition = Math.max(0, Math.min(19, Math.round(state.fretboardPosition)));
  state.fretboardSpan = Math.max(3, Math.min(7, Math.round(state.fretboardSpan)));
  renderTranscription();
  updateFretboardPlayback();
}

function getFretboardLayout(stem) {
  const isBass = stem === 'bass';
  const strings = isBass
    ? [{ name: 'G', midi: 43 }, { name: 'D', midi: 38 }, { name: 'A', midi: 33 }, { name: 'E', midi: 28 }]
    : [{ name: 'E', midi: 64 }, { name: 'B', midi: 59 }, { name: 'G', midi: 55 }, { name: 'D', midi: 50 }, { name: 'A', midi: 45 }, { name: 'E', midi: 40 }];
  const position = Math.max(0, Math.min(19, Math.round(state.fretboardPosition)));
  const span = Math.max(3, Math.min(7, Math.round(state.fretboardSpan)));
  const frets = [];
  for (let index = 0; index <= span; index += 1) {
    frets.push({ fret: position + index, left: 8 + (index / span) * 84 });
  }
  return {
    label: isBass ? 'stem.bass' : 'stem.guitar',
    strings,
    position,
    span,
    frets,
    minFret: position,
    maxFret: position + span - 1
  };
}

function buildFretboardNotes(notes, layout) {
  let previous = null;
  return (notes || []).map((note) => {
    const pitch = Math.round(Number(note.midiPitch) || 0);
    const allCandidates = [];
    layout.strings.forEach((stringInfo, stringIndex) => {
      const fret = pitch - stringInfo.midi;
      if (fret < 0 || fret > 24) return;
      allCandidates.push({
        stringIndex,
        stringName: stringInfo.name,
        fret
      });
    });
    const inBox = allCandidates.filter((candidate) => (
      candidate.fret >= layout.minFret && candidate.fret <= layout.maxFret
    ));
    if (!inBox.length) return null;

    const primary = chooseFretboardPrimary(inBox, previous, layout);
    previous = primary;
    const alternatives = inBox.filter((candidate) => (
      candidate.stringIndex !== primary.stringIndex || candidate.fret !== primary.fret
    ));
    return { note, primary, alternatives };
  }).filter(Boolean);
}

function chooseFretboardPrimary(candidates, previous, layout) {
  const boxCenter = layout.minFret + (layout.span - 1) / 2;
  let best = candidates[0];
  let bestScore = Infinity;
  candidates.forEach((candidate) => {
    const score = previous
      ? Math.abs(candidate.fret - previous.fret) * 0.85 +
        Math.abs(candidate.stringIndex - previous.stringIndex) * 1.55 +
        Math.abs(candidate.fret - boxCenter) * 0.08
      : Math.abs(candidate.fret - boxCenter) +
        Math.abs(candidate.stringIndex - (layout.strings.length - 1) / 2) * 0.25;
    if (score < bestScore) {
      bestScore = score;
      best = candidate;
    }
  });
  return best;
}

function getFretboardStringY(index, count) {
  if (count <= 1) return 50;
  return 16 + (index / (count - 1)) * 68;
}

function renderSheet(notes, stem) {
  const view = els.sheetView;
  if (!view) return;
  view.innerHTML = '';
  if (!notes.length) {
    const empty = document.createElement('div');
    empty.className = 'empty-state';
    empty.textContent = getTranscriptionEmptyMessage(stem);
    view.appendChild(empty);
    state.sheet = null;
    return;
  }

  const scroll = document.createElement('div');
  const strip = document.createElement('div');
  const guideLayer = document.createElement('div');
  const chordLayer = document.createElement('div');
  const playhead = document.createElement('span');
  scroll.className = 'sheet-scroll';
  strip.className = 'sheet-strip';
  guideLayer.className = 'sheet-guide-layer';
  chordLayer.className = 'sheet-chord-layer';
  playhead.className = 'sheet-playhead';
  scroll.appendChild(strip);
  strip.appendChild(guideLayer);
  strip.appendChild(chordLayer);
  strip.appendChild(playhead);
  view.appendChild(scroll);

  const measureWidth = 230;
  const score = buildSheetScore(notes);
  const timelineWidth = Math.max(measureWidth, score.measures.length * measureWidth);
  strip.style.width = `${timelineWidth + 20}px`;
  const entries = [];
  score.measures.forEach((measure) => {
    const el = document.createElement('div');
    el.className = 'sheet-measure';
    el.dataset.start = measure.start;
    el.dataset.end = measure.end;
    el.style.width = `${measureWidth}px`;
    const stave = document.createElement('div');
    stave.className = 'sheet-stave';
    el.appendChild(stave);
    strip.appendChild(el);
    if (!drawVexMeasure(stave, measure, score, measureWidth)) drawDomMeasure(stave, measure);
    measure.el = el;
    entries.push({ start: measure.start, end: measure.end, el });
  });
  renderSheetGuides(guideLayer, score, measureWidth);
  renderSheetChords(chordLayer, score, measureWidth);
  state.sheet = { scroll, strip, playhead, measures: entries, score, measureWidth };
}

function buildSheetScore(notes) {
  const measures = getSheetMeasures();
  measures.forEach((measure) => {
    measure.events = [];
    measure.chords = state.chords.filter((segment) => {
      const start = Number(segment.start) || 0;
      return start >= measure.start && start < measure.end;
    });
  });

  notes.forEach((note) => {
    const start = Number(note.startTime) || 0;
    const end = Math.max(start + 0.04, Number(note.endTime) || start + 0.08);
    for (let index = 0; index < measures.length; index += 1) {
      const measure = measures[index];
      const alreadyPlayingAtFirstMeasure = index === 0 && start < measure.start && end > measure.start;
      if (!alreadyPlayingAtFirstMeasure && (start < measure.start || start >= measure.end)) continue;
      const measureDuration = Math.max(0.1, measure.end - measure.start);
      const slotSize = measureDuration / 16;
      const startSlot = alreadyPlayingAtFirstMeasure ? 0 : Math.max(0, Math.min(15, Math.round((start - measure.start) / slotSize)));
      measure.events.push({
        startSlot,
        slots: 1,
        midiPitch: note.midiPitch,
        startTime: start,
        endTime: start + slotSize
      });
      break;
    }
  });

  measures.forEach((measure) => {
    measure.events = compactSheetEvents(measure.events);
    measure.notes = measure.events.flatMap((event) => event.midiPitches.map((midiPitch) => ({ midiPitch })));
  });

  return {
    timeSignature: '4/4',
    clef: averagePitch(notes) < 57 ? 'bass' : 'treble',
    measures,
    start: measures.length ? measures[0].start : 0,
    end: measures.length ? measures[measures.length - 1].end : 0
  };
}

function buildMeasures(notes) {
  const starts = getMeasureStarts();
  return starts.slice(0, 64).map((start, index) => {
    const end = starts[index + 1] || start + estimateMeasureLength();
    return {
      index,
      start,
      end,
      notes: notes.filter((note) => note.startTime >= start && note.startTime < end).slice(0, 24)
    };
  });
}

function getSheetMeasures() {
  const starts = getMeasureStarts();
  const averageMeasure = estimateMeasureLength();
  const duration = state.duration || 0;
  const endLimit = Math.max(duration, (starts[starts.length - 1] || 0) + averageMeasure);
  const measures = starts.slice(0, 64).map((start, index) => {
    const end = starts[index + 1] || Math.min(endLimit, start + averageMeasure);
    if (end <= start + 0.05) return null;
    return {
      index,
      start: Math.max(0, start),
      end
    };
  }).filter(Boolean);
  return measures.length ? measures : [{ index: 0, start: 0, end: Math.max(2, duration || averageMeasure) }];
}

function getMeasureStarts() {
  const downbeats = state.beats.filter((beat) => beat.isDownbeat).map((beat) => beat.time);
  if (downbeats.length) return downbeats[0] > 0.08 ? [0].concat(downbeats) : downbeats;
  const length = estimateMeasureLength();
  const starts = [];
  for (let t = 0; t < state.duration; t += length) starts.push(t);
  return starts.length ? starts : [0];
}

function estimateMeasureLength() {
  if (state.beats.length > 1) return Math.max(0.5, (state.beats[1].time - state.beats[0].time) * 4);
  return state.tempo ? 240 / state.tempo : 2;
}

function drawVexMeasure(container, measure, score, measureWidth) {
  const VF = VexFlow;
  if (!VF?.Renderer || !container) return false;
  container.innerHTML = '';
  try {
    const renderer = new VF.Renderer(container, VF.Renderer.Backends.SVG);
    const width = Math.max(180, Number(measureWidth) || 230);
    const height = 170;
    renderer.resize(width, height);
    const context = renderer.getContext();
    const stave = new VF.Stave(measure.index === 0 ? 8 : 0, 48, width - (measure.index === 0 ? 8 : 0));
    if (measure.index === 0) stave.addClef(score.clef).addTimeSignature(score.timeSignature);
    stave.setContext(context).draw();
    const tickables = buildSheetTickables(VF, measure, score.clef);
    const voice = new VF.Voice({ num_beats: 4, beat_value: 4 }).setStrict(false);
    voice.addTickables(tickables.length ? tickables : [createSheetGhostNote(VF, 16)]);
    new VF.Formatter().joinVoices([voice]).format([voice], width - (measure.index === 0 ? 92 : 42));
    voice.draw(context, stave);
    return true;
  } catch (error) {
    console.warn('FiveLoop Studio: sheet render failed', error);
    return false;
  }
}

function compactSheetEvents(events) {
  const bySlot = {};
  events.forEach((event) => {
    const key = String(event.startSlot);
    if (!bySlot[key]) {
      bySlot[key] = {
        startSlot: event.startSlot,
        slots: 1,
        midiPitches: [],
        startTime: event.startTime,
        endTime: event.endTime
      };
    }
    bySlot[key].endTime = Math.max(bySlot[key].endTime, event.endTime);
    if (!bySlot[key].midiPitches.includes(event.midiPitch)) bySlot[key].midiPitches.push(event.midiPitch);
  });
  return Object.keys(bySlot).map((key) => {
    const event = bySlot[key];
    event.midiPitches.sort((a, b) => a - b);
    return event;
  }).sort((a, b) => a.startSlot - b.startSlot);
}

function buildSheetTickables(VF, measure, clef) {
  const tickables = [];
  let cursor = 0;
  measure.events.forEach((event) => {
    const start = Math.max(cursor, Math.min(15, event.startSlot));
    if (start > cursor) appendSheetGhostTickables(VF, tickables, start - cursor);
    const keys = event.midiPitches.map(vexKey).filter(Boolean);
    if (!keys.length) {
      appendSheetGhostTickables(VF, tickables, 1);
    } else {
      const note = new VF.StaveNote({ clef, keys, duration: '16' });
      if (note.addClass) note.addClass('sheet-onset-note');
      note.shouldDrawFlag = () => false;
      note.drawFlag = () => {};
      if (note.setStemStyle) note.setStemStyle({ strokeStyle: 'rgba(0,0,0,0)', fillStyle: 'rgba(0,0,0,0)' });
      if (note.setFlagStyle) note.setFlagStyle({ strokeStyle: 'rgba(0,0,0,0)', fillStyle: 'rgba(0,0,0,0)' });
      keys.forEach((key, index) => {
        if (key.includes('#')) note.addModifier(new VF.Accidental('#'), index);
      });
      tickables.push(note);
    }
    cursor = start + 1;
  });
  if (cursor < 16) appendSheetGhostTickables(VF, tickables, 16 - cursor);
  return tickables;
}

function appendSheetGhostTickables(VF, tickables, slots) {
  let remaining = Math.max(0, Math.round(slots));
  while (remaining > 0) {
    tickables.push(createSheetGhostNote(VF, 1));
    remaining -= 1;
  }
}

function createSheetGhostNote(VF, slots) {
  return new VF.GhostNote(sheetDurationFromSlots(slots).vex);
}

function sheetDurationFromSlots(slots) {
  const allowed = [
    { slots: 16, vex: 'w' },
    { slots: 12, vex: 'hd' },
    { slots: 8, vex: 'h' },
    { slots: 6, vex: 'qd' },
    { slots: 4, vex: 'q' },
    { slots: 3, vex: '8d' },
    { slots: 2, vex: '8' },
    { slots: 1, vex: '16' }
  ];
  const target = Math.max(1, Math.min(16, Math.round(slots)));
  return allowed.reduce((best, candidate) => (
    Math.abs(candidate.slots - target) < Math.abs(best.slots - target) ? candidate : best
  ), allowed[allowed.length - 1]);
}

function renderSheetGuides(layer, score, measureWidth) {
  if (!layer || !score?.measures?.length) return;
  layer.innerHTML = '';
  state.beats.forEach((beat) => {
    const time = Number(beat?.time);
    if (!isFinite(time) || time < score.start || time > score.end) return;
    const line = document.createElement('span');
    line.className = `sheet-beat-line${beat.isDownbeat ? ' sheet-beat-line-downbeat' : ''}`;
    line.style.left = `${getSheetXForTime(time, score, measureWidth)}px`;
    layer.appendChild(line);
  });
}

function renderSheetChords(layer, score, measureWidth) {
  if (!layer || !score?.measures?.length) return;
  layer.innerHTML = '';
  const duration = state.duration || score.end || 0;
  const usedBeatSlots = {};
  state.chords.forEach((segment) => {
    const start = Math.max(0, Math.min(duration, Number(segment.start) || 0));
    const end = Math.max(start, Math.min(duration, Number(segment.end) || start));
    const chord = transposeChord(segment.chord, state.pitch);
    if (!chord || isNoChordLabel(chord) || end <= start || isChordHiddenByPremium(start)) return;
    const displayTime = getChordLabelTime(start, usedBeatSlots);
    if (isChordHiddenByPremium(displayTime)) return;
    const x = getSheetXForTime(displayTime, score, measureWidth);
    const label = document.createElement('span');
    label.className = 'sheet-chord';
    label.textContent = chord;
    label.style.left = `${Math.max(4, Math.min(score.measures.length * measureWidth - 34, x))}px`;
    layer.appendChild(label);
  });
}

function getSheetXForTime(time, score, measureWidth) {
  const measures = score?.measures || [];
  if (!measures.length) return 0;
  const clamped = Math.max(measures[0].start, Math.min(measures[measures.length - 1].end, Number(time) || 0));
  let measure = measures[measures.length - 1];
  for (let index = 0; index < measures.length; index += 1) {
    if (clamped >= measures[index].start && clamped <= measures[index].end) {
      measure = measures[index];
      break;
    }
  }
  const local = (clamped - measure.start) / Math.max(0.05, measure.end - measure.start);
  return measure.index * measureWidth + Math.max(0, Math.min(1, local)) * measureWidth;
}

function drawDomMeasure(container, measure) {
  for (let i = 0; i < 5; i += 1) {
    const line = document.createElement('span');
    line.className = 'sheet-staff-line';
    line.style.top = `${48 + i * 16}px`;
    container.appendChild(line);
  }
  const span = Math.max(0.1, measure.end - measure.start);
  measure.notes.forEach((note) => {
    const el = document.createElement('span');
    el.className = 'sheet-note';
    el.style.left = `${((note.startTime - measure.start) / span) * 180 + 18}px`;
    el.style.top = `${106 - (note.midiPitch - 48) * 2.1}px`;
    container.appendChild(el);
  });
}

function updateTranscriptionPlayback() {
  updateTranscriptionPremiumOverlayVisibility();
  updatePianoPlayback();
  updateFretboardPlayback();
  updateSheetPlayback();
}

function updatePianoPlayback() {
  const piano = state.piano;
  if (!piano || !piano.notes.length) return;
  const current = getWaveformDisplayPosition();
  const loopStart = getActiveLoopStart();
  const loopEnd = getActiveLoopEnd() || state.duration || 0;
  const loopLength = Math.max(0.05, loopEnd - loopStart);
  const lookahead = Math.max(0.5, piano.lookahead || 2.6);
  const rollHeight = (piano.notesLayer && piano.notesLayer.clientHeight) || piano.rollHeight || 177;
  const activeKeys = {};

  piano.notes.forEach((entry) => {
    const note = entry.note;
    const el = entry.element;
    const originalStart = note.startTime || 0;
    const originalEnd = note.endTime || originalStart + 0.08;
    if (originalEnd <= loopStart || originalStart >= loopEnd) {
      el.style.display = 'none';
      return;
    }
    const noteStart = Math.max(loopStart, Math.min(loopEnd, originalStart));
    const noteEnd = Math.min(loopEnd, Math.max(noteStart + 0.04, originalEnd));

    let raw = noteStart - current;
    while (raw < -(noteEnd - noteStart) && loopLength > 0) raw += loopLength;
    while (raw > loopLength && loopLength > 0) raw -= loopLength;

    const duration = Math.max(0.04, noteEnd - noteStart);
    const playing = raw <= 0 && raw > -duration;
    const visible = raw <= lookahead && raw > -duration - 0.02;
    if (!visible) {
      el.style.display = 'none';
      return;
    }

    const yBottom = rollHeight * (1 - raw / lookahead);
    const height = Math.max(7, Math.min(rollHeight * 0.65, (duration / lookahead) * rollHeight));
    el.style.display = 'block';
    el.style.top = `${yBottom - height}px`;
    el.style.height = `${height}px`;
    el.style.opacity = playing ? '1' : entry.velocityAlpha;
    el.classList.toggle('vfcRollNotePlaying', playing);
    if (playing) activeKeys[entry.key.id] = true;
  });

  piano.keys.forEach((key) => {
    if (!key.element) return;
    key.element.classList.toggle('vfcPianoKeyActive', !!activeKeys[key.id]);
  });
}

function updateFretboardPlayback() {
  const fretboard = state.fretboard;
  if (!fretboard || !fretboard.notes?.length) return;
  const current = getWaveformDisplayPosition();
  const loopStart = getActiveLoopStart();
  const loopEnd = getActiveLoopEnd() || state.duration || 0;
  const loopLength = Math.max(0.05, loopEnd - loopStart);
  const lookahead = Math.max(0.5, fretboard.lookahead || 2.8);

  fretboard.notes.forEach((entry) => {
    const note = entry.note;
    const el = entry.element;
    const originalStart = note.startTime || 0;
    const originalEnd = note.endTime || originalStart + 0.08;
    if (originalEnd <= loopStart || originalStart >= loopEnd) {
      el.style.display = 'none';
      return;
    }

    const noteStart = Math.max(loopStart, Math.min(loopEnd, originalStart));
    const noteEnd = Math.min(loopEnd, Math.max(noteStart + 0.04, originalEnd));
    let raw = noteStart - current;
    while (raw < -(noteEnd - noteStart) && loopLength > 0) raw += loopLength;
    while (raw > loopLength && loopLength > 0) raw -= loopLength;

    const duration = Math.max(0.04, noteEnd - noteStart);
    const playing = raw <= 0 && raw > -duration;
    const visible = raw <= lookahead && raw > -duration - 0.02;
    el.style.display = visible ? 'flex' : 'none';
    if (visible) {
      const targetX = fretboard.hitX || 22;
      const travel = 76 - targetX;
      const x = targetX + Math.max(0, raw / lookahead) * travel;
      const y = getFretboardStringY(entry.candidate.stringIndex, fretboard.strings.length);
      el.style.left = `${x}%`;
      el.style.top = `${y}%`;
      el.style.opacity = playing ? '1' : (entry.isPrimary ? entry.velocityAlpha : Math.min(0.45, entry.velocityAlpha * 0.55));
    }
    el.classList.toggle('is-playing', playing);
  });
}

function updateSheetPlayback() {
  const sheet = state.sheet;
  if (!sheet) return;
  const current = getWaveformDisplayPosition();
  const measure = sheet.measures.find((item) => current >= item.start && current < item.end) || sheet.measures[0];
  if (!measure) return;
  sheet.measures.forEach((item) => item.el?.classList.toggle('sheet-measure-active', item === measure));
  const x = getSheetXForTime(current, sheet.score, sheet.measureWidth);
  sheet.playhead.style.left = `${x + 10}px`;
  sheet.playhead.style.opacity = measure ? '1' : '0';
  if (state.playing || dragState?.type === 'playhead') {
    sheet.scroll.scrollLeft = Math.max(0, x - sheet.scroll.clientWidth * 0.35);
  }
}

function startAnimationLoop() {
  stopAnimationLoop();
  const tick = () => {
    const current = getPosition();
    if (!state.loopOn && state.playing && current >= state.duration - 0.03) {
      pausePlayback();
      state.pausedAt = 0;
      return;
    }
    updateWaveformPlayhead();
    syncWaveformScrollToPlayback();
    updateMixerMeters();
    const now = performance.now();
    if (now - lastTranscriptionFrameAt > 33) {
      updateTranscriptionPlayback();
      lastTranscriptionFrameAt = now;
    }
    updateTimeLabels();
    rafId = requestAnimationFrame(tick);
  };
  rafId = requestAnimationFrame(tick);
}

function stopAnimationLoop() {
  if (rafId) cancelAnimationFrame(rafId);
  rafId = 0;
}

function updateTransport() {
  if (els.playBtn) els.playBtn.textContent = state.playing ? '⏸' : '▶';
}

function updateTimeLabels() {
  const current = formatTime(getWaveformDisplayPosition());
  const total = formatTime(state.duration);
  if (els.currentTimeLabel) els.currentTimeLabel.textContent = current;
  if (els.totalTimeLabel) els.totalTimeLabel.textContent = total;
  if (els.durationLabel) els.durationLabel.textContent = total;
}

function exportStem(name) {
  const raw = state.rawBuffers[name];
  if (!raw) {
    console.log('Audio data is not available for download.');
    return;
  }
  const blob = new Blob([raw], { type: 'audio/mpeg' });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = `${name}.mp3`;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
}

function setLoadingText(text) {
  if (els.waveformLoading) els.waveformLoading.textContent = text;
}

function formatTime(seconds) {
  if (!isFinite(seconds) || seconds <= 0) return '--:--';
  const total = Math.floor(seconds);
  const minutes = Math.floor(total / 60);
  const secs = total % 60;
  return `${minutes}:${String(secs).padStart(2, '0')}`;
}

function averagePitch(notes) {
  if (!notes.length) return 60;
  return notes.reduce((sum, note) => sum + note.midiPitch, 0) / notes.length;
}

function vexKey(midi) {
  const pitch = Math.max(0, Math.min(127, Math.round(Number(midi) || 60)));
  return `${NOTE_NAMES[pitch % 12].toLowerCase()}/${Math.floor(pitch / 12) - 1}`;
}

function transposeChord(chord, semitones) {
  chord = String(chord || '').trim();
  semitones = Math.round(Number(semitones) || 0);
  if (!chord || !semitones) return chord;
  return chord.replace(/^([A-Ga-g])([#b]?)/, (match, letter, accidental) => {
    const pc = pitchClass(letter, accidental);
    const preferFlats = accidental === 'b';
    const names = preferFlats
      ? ['C', 'Db', 'D', 'Eb', 'E', 'F', 'Gb', 'G', 'Ab', 'A', 'Bb', 'B']
      : NOTE_NAMES;
    return names[((pc + semitones) % 12 + 12) % 12];
  });
}

function isNoChordLabel(chord) {
  const normalized = String(chord || '').trim().replace(/\s+/g, '').toUpperCase();
  return normalized === 'N' || normalized === 'NC' || normalized === 'N.C.' || normalized === 'NOCHORD';
}

function pitchClass(letter, accidental) {
  const base = { C: 0, D: 2, E: 4, F: 5, G: 7, A: 9, B: 11 };
  let pc = base[String(letter || '').toUpperCase()] ?? 0;
  if (accidental === '#') pc += 1;
  if (accidental === 'b') pc -= 1;
  return ((pc % 12) + 12) % 12;
}

function roundRect(ctx, x, y, width, height, radius) {
  ctx.beginPath();
  ctx.moveTo(x + radius, y);
  ctx.arcTo(x + width, y, x + width, y + height, radius);
  ctx.arcTo(x + width, y + height, x, y + height, radius);
  ctx.arcTo(x, y + height, x, y, radius);
  ctx.arcTo(x, y, x + width, y, radius);
  ctx.closePath();
}

function storagePath(songId, filename) {
  const base = isDemoSong(songId) ? 'demoSongs' : 'globalSongs';
  return `${base}/${songId}/${filename}`;
}

function songDocRef(dbRef, songId) {
  if (isDemoSong(songId)) return doc(dbRef, 'demoSongs', songId);
  return doc(dbRef, 'globalSongs', songId);
}

function isDemoSong(id) {
  return typeof id === 'string' && id.toLowerCase().includes('demosong');
}

function getUrlParameter(name) {
  const params = new URLSearchParams(window.location.search);
  return params.get(name);
}

function capitalize(text) {
  return text.charAt(0).toUpperCase() + text.slice(1);
}
